import { useState, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import {
  GitPullRequest,
  Database,
  Sparkles,
  ChevronRight,
  Layers,
  RefreshCw,
  Bot,
  UserPlus,
  LockKeyhole,
  ArrowLeft,
  Search,
  CheckCircle2,
  CircleAlert,
  Wifi,
} from "lucide-react";
import { fetchEvolutionProposals } from "../lib/evolution";
import { DEFAULT_BRAND_COLOR } from "../lib/brand";
import { safeInitial } from "../lib/initial";
import { ASSISTANT_PROFILES, isAssistantHiddenFromNav } from "../agents/registry";
import { connectorRepairState, type Connection as IntegrationConnection } from "./schema-renderer/IntegrationHub";

// ─────────────────────────────────────────────────────────────────────────
// Mission Control — the decision-first landing screen AND the marketing
// screenshot: the cockpit of a staffed operating system. Pinned first in the
// Workspace nav and the shell's default activeId.
//
// Premium dark language (matches the generated apps): #0A0A0A base, glass cards
// (border-white/10 + faint backdrop blur over a near-black surface), the Agentic Operating System
// brand color as a DISCIPLINED single accent (decision count, Review/CTA,
// avatar tints, timeline dots, active states — not everywhere), mono for
// numbers, a fade-in stagger on mount.
//
// Composed entirely from EXISTING reads — no new tables, no new endpoints:
//   1. DECISIONS      — pending os_evolution_proposals (owner-scoped client read).
//                       Review → The Architect's Evolution tab.
//   2. THE PULSE      — per-collection screen_data counts (owner-scoped read).
//   3. NEEDS ATTENTION— deep-links to The Operator's chat with a focus prompt.
//   4. STAFF BOARD    — latest action per assistant from /api/audit/actions.
//   5. RECEIPTS       — 10 most recent actions, attributed + relative time.
//
// Handoffs use the shell's activeId mechanics via onNavigateAssistant. Every
// section keeps a designed skeleton + a one-line empty state — no dead voids.
// ─────────────────────────────────────────────────────────────────────────

type AssistantNavTarget = { tab?: string; prompt?: string };

type Proposal = {
  id: string;
  type: string;
  title: string;
  rationale: string | null;
  created_at: string;
  schema_diff?: {
    agent_id?: string;
    agent_name?: string;
    department?: string;
  } | null;
};

type ActionRow = {
  id: string;
  tool_called: string;
  tool_response?: any;
  claim_summary: string | null;
  assistant_id: string | null;
  created_at: string;
};

type DeployedAgent = {
  id: string;
  title: string;
  description: string | null;
  schema_diff: {
    agent_id?: string;
    agent_name?: string;
    department?: string;
    requested_access?: string;
  } | null;
  applied_at: string | null;
  created_at: string;
};

type WorkspacePulseScreen = {
  screen_id: string;
  label: string;
};

export type LaunchReadinessAction = "integrations" | "decisions" | "schema" | "operator" | null;

export type LaunchReadinessItem = {
  id: string;
  title: string;
  detail: string;
  status: "ready" | "needs_action" | "watching";
  action: LaunchReadinessAction;
  cta: string | null;
  action_detail: {
    why: string;
    involves: string;
    effect: string;
    approval: string;
  } | null;
};

export type LaunchReadinessInput = {
  connectionCount: number;
  connectorRepairCount: number;
  screenCount: number;
  rowCount: number;
  pendingDecisionCount: number;
  receiptCount: number;
  schemaStandardReady: boolean;
  canRequestSchemaStandardUpgrade: boolean;
  needsCodeBundleStandardReview: boolean;
};

type ConnectorHealthSummary = {
  total_count: number;
  ready_count: number;
  repair_needed_count: number;
  expired_count: number;
  error_count: number;
  missing_scope_count: number;
  missing_capabilities: string[];
  providers: Array<{
    provider: string;
    status: "ready" | "needs_repair";
    label: string;
    missing_capabilities: string[];
    expires_at: string | null;
  }>;
};

type StaffThreadSignal = {
  unread_count: number;
  last_message_preview: string | null;
  last_message_at: string | null;
};

const CURRENT_SCHEMA_STANDARD_VERSION = "2026-06-07";

const ACTION_LABELS: Record<string, string> = {
  create_record: "Created a record",
  update_record: "Updated a record",
  delete_record: "Deleted a record",
  desktop_permission_granted: "Granted local access",
  desktop_permission_revoked: "Revoked local access",
  schema_evolution_applied: "Applied an Agentic Operating System update",
  gmail_create_draft: "Drafted an email",
  google_drive_import_csv_rows: "Synced CSV file",
  google_sheets_import_rows: "Synced Google Sheet",
  assignment_run: "Ran assignment",
  media_generate: "Generated media",
  triage_collection: "Ranked priorities",
  project_outlook: "Projected outlook",
};

function humanize(id: string): string {
  return id
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function specialistAssistantId(agentId: string | undefined): string | null {
  if (!agentId) return null;
  const clean = String(agentId).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return clean ? `specialist_${clean}` : null;
}

function actionLabel(toolCalled: string): string {
  return ACTION_LABELS[toolCalled] || humanize(toolCalled);
}

export function receiptSummary(action: Pick<ActionRow, "tool_called" | "tool_response" | "claim_summary">): string {
  const preview = action.tool_called === "schema_evolution_applied"
    ? action.tool_response?.notification?.preview
    : null;
  return String(preview || action.claim_summary || actionLabel(action.tool_called));
}

export function receiptNavAssistant(action: Pick<ActionRow, "tool_called" | "tool_response" | "assistant_id">): string | null {
  if (action.tool_called === "schema_evolution_applied") {
    return String(action.tool_response?.notification?.assistant_id || action.assistant_id || "").trim() || null;
  }
  return String(action.assistant_id || "").trim() || null;
}

function relTime(iso: string | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function greetingFor(date: Date): string {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

// id → display name; the default writer (Operator) owns unattributed/legacy rows.
const DEFAULT_WRITER = ASSISTANT_PROFILES.find((p) => !p.is_system);
function assistantName(id: string | null): string {
  if (id) {
    const p = ASSISTANT_PROFILES.find((a) => a.id === id);
    if (p) return p.display_name;
  }
  return DEFAULT_WRITER?.display_name || "The Operator";
}
// Disciplined accent: distinguish agents by stepping opacity of the single brand
// color rather than introducing a second hue.
function assistantOpacity(id: string | null): number {
  const idx = id ? ASSISTANT_PROFILES.findIndex((a) => a.id === id) : 0;
  return Math.max(0.4, 1 - Math.max(0, idx) * 0.22);
}

function filterLocalAccessReferences(items?: string[] | null): string[] {
  return (items || []).filter((item) => !/desktop agent/i.test(item));
}

// Glass-card surface — near-black over the #0A0A0A base, hairline border, faint
// blur. The hover variant brightens the border + lifts for "this is clickable".
const CARD = "rounded-xl border border-white/10 bg-[#141414]/70 backdrop-blur-sm";
const CARD_INTERACTIVE =
  CARD +
  " cursor-pointer transition-all duration-200 hover:border-white/20 hover:bg-[#1a1a1a]/80 hover:-translate-y-0.5";

export type LibraryAgent = {
  id: string;
  name: string;
  department: string;
  description: string;
  ask: string;
  recommended?: boolean;
  skill_ids?: string[];
  required_screens?: string[];
  starter_tasks?: string[];
  recommended_integrations?: string[];
  success_metrics?: string[];
  studio_enabled?: boolean;
  studio_kind?: "code" | "content";
};

export type RecommendedAgentMatch = {
  agent: LibraryAgent;
  reason: string;
  evidenceState?: "known_context" | "research_required";
};

type PendingAgentRequest = {
  decisionId: string;
  agent: LibraryAgent | null;
  name: string;
  department: string;
  createdAt: string;
};

const UNIVERSAL_AGENT_SKILLS = ["web_research", "source_verification", "quick_media"] as const;

function clipProposalText(value: string, maxLength: number) {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
}

function withUniversalAgentSkills(skillIds?: string[]): string[] {
  return Array.from(new Set([...UNIVERSAL_AGENT_SKILLS, ...(skillIds || [])]));
}

export const LIBRARY_AGENTS: LibraryAgent[] = [
  {
    id: "scouting_analyst",
    name: "Scouting Analyst",
    department: "Football",
    description: "Ranks prospects, flags gaps, and turns scouting notes into a clear watchlist.",
    ask: "Propose deploying a Scouting Analyst for the Agentic Operating System. Put it under Decisions first, with why it fits and what access it needs.",
    recommended: true,
    skill_ids: ["workspace_data_read", "football_scouting", "screen_triage", "failure_diagnosis"],
    required_screens: ["Players", "Scouting Reports", "Watchlist", "Development Plans"],
    starter_tasks: [
      "Rank the current player watchlist and explain the evidence behind the top five.",
      "Find prospects stuck too long in one stage and recommend the next scouting action.",
      "Show which player records are missing position, stage, age, or latest report.",
    ],
    recommended_integrations: ["Workspace sheets", "Workspace files"],
    success_metrics: [
      "Ranks players using real scouting rows.",
      "Flags missing scouting evidence instead of guessing.",
      "Turns repeated scouting gaps into a Decision proposal.",
    ],
  },
  {
    id: "player_development",
    name: "Player Development Agent",
    department: "Performance",
    description: "Tracks player progress, risk signals, and next development actions.",
    ask: "Propose deploying a Player Development Agent for the Agentic Operating System. Put it under Decisions first, with why it fits and what access it needs.",
    recommended: true,
    skill_ids: ["workspace_data_read", "football_scouting", "screen_triage", "outlook_projection", "failure_diagnosis"],
    required_screens: ["Players", "Development Plans", "Training Notes", "Risk Signals"],
    starter_tasks: [
      "Review development progress and separate verified facts from missing history.",
      "Find players who need a next focus this week.",
      "Project which development plans are at risk of stalling.",
    ],
    recommended_integrations: ["Workspace sheets", "Workspace files", "Calendar"],
    success_metrics: [
      "Shows verified development progress from player rows and notes.",
      "Flags risk signals with the missing evidence called out.",
      "Turns repeat development gaps into screen or assignment Decisions.",
    ],
  },
  {
    id: "contract_admin",
    name: "Contract Admin Agent",
    department: "Operations",
    description: "Watches contract dates, paperwork, follow-ups, and renewal moments.",
    ask: "Propose deploying a Contract Admin Agent for the Agentic Operating System. Put it under Decisions first, with why it fits and what access it needs.",
    recommended: true,
    skill_ids: ["workspace_data_read", "screen_triage", "document_generation", "failure_diagnosis"],
    required_screens: ["Contracts", "Documents", "Renewals", "Follow-ups"],
    starter_tasks: [
      "Find contracts with missing paperwork, stale follow-ups, or upcoming renewal dates.",
      "Show documents that need user attention before the next deadline.",
      "Recommend one contract workflow improvement for approval.",
    ],
    recommended_integrations: ["Workspace files", "Gmail", "Calendar"],
    success_metrics: [
      "Tracks renewal and paperwork risk from dated records.",
      "Routes missing files to Documents or approved local access.",
      "Leaves receipts when contract follow-ups or drafts are created.",
    ],
  },
  {
    id: "launch_operator",
    name: "Launch Operator",
    department: "Launch",
    description: "Turns product readiness, pilots, bugs, feedback, deployments, and founder decisions into one launch command center.",
    ask: "Recommend whether a Launch Operator should be deployed for the Agentic Operating System. Put any deployment under Decisions first, with screens for launch initiatives, product readiness, demos, bugs, feedback, deployments, and founder decisions.",
    recommended: true,
    skill_ids: ["workspace_data_read", "record_operations", "screen_triage", "outlook_projection", "audience_research", "copywriting", "creative_testing", "launch_rollout_planning", "failure_diagnosis"],
    required_screens: ["Launch Initiatives", "Rollout Plan", "Product Readiness", "Demo & Pilot", "Bug Fixes", "User Feedback", "Deployments", "Founder Decisions"],
    starter_tasks: [
      "Rank launch blockers by proof gate, customer impact, and what must happen next.",
      "Turn pilot notes, bugs, and feedback into Launch Initiatives records with owners and stage.",
      "Show which launch promises are ready for strangers and which are still founder-dependent.",
    ],
    recommended_integrations: ["Workspace files", "Gmail", "Workspace sheets"],
    success_metrics: [
      "Keeps launch work tied to proof gates, receipts, and real user evidence.",
      "Routes content, UGC, sales, support, and product gaps to the right specialist instead of doing every job itself.",
      "Uses the user's niche language and never imports irrelevant vertical playbooks without evidence.",
    ],
  },
  {
    id: "coding_agent",
    name: "Coding Agent",
    department: "Code",
    description: "Builds, modifies, debugs, and previews websites, landing pages, apps, and code-backed OS screens from approved source context.",
    ask: "Recommend whether a Coding Agent should be deployed for the Agentic Operating System. Put any deployment under Decisions first, with a Studio tab for website/app work, source access, previews, verification, and receipts.",
    recommended: true,
    studio_enabled: true,
    studio_kind: "code",
    skill_ids: ["workspace_data_read", "web_research", "source_verification", "codebase_modification", "website_studio", "desktop_agent_bridge", "failure_diagnosis"],
    required_screens: ["Code Studio", "Website Briefs", "Change Requests", "Preview Checks", "Deployments", "Receipts"],
    starter_tasks: [
      "Build a launch website or landing page from the approved brand, audience, offer, and proof.",
      "Review an existing website URL or local project folder and propose safe improvements before editing.",
      "Modify a page/component, verify it with preview/build checks, and leave a receipt of what changed.",
    ],
    recommended_integrations: ["GitHub", "Workspace files", "Vercel/Netlify"],
    success_metrics: [
      "Every code change starts from an approved URL, repo, folder, or brief.",
      "Shows diffs, preview checks, build/test results, and deployment status before claiming success.",
      "Keeps website/app work inside its code Studio; non-code agents route code changes here.",
    ],
  },
  {
    id: "real_estate_operator",
    name: "Real Estate Operator",
    department: "Real Estate",
    description: "Watches listings, buyers, viewings, offers, documents, and stale follow-ups.",
    ask: "Propose deploying a Real Estate Operator for the Agentic Operating System. Put it under Decisions first, with screens for listings, deals, documents, and listing campaigns.",
    recommended: true,
    skill_ids: ["workspace_data_read", "real_estate_pipeline", "screen_triage", "outlook_projection", "failure_diagnosis"],
    required_screens: ["Listings", "Deals Pipeline", "Viewings", "Documents", "Listing Campaigns"],
    starter_tasks: [
      "Find listings or deals that need attention first and cite the exact row facts.",
      "Show stale follow-ups across buyers, sellers, tenants, and offers.",
      "Tell me which listing needs a campaign refresh and why.",
    ],
    recommended_integrations: ["Workspace files", "Workspace sheets", "Gmail", "Calendar"],
    success_metrics: [
      "Flags stale listings, missing docs, and next follow-ups from real rows.",
      "Keeps repeatable listing work in Workspace screens, not buried in chat.",
      "Requests the right data source or permission when blocked.",
    ],
  },
  {
    id: "sdr",
    name: "Sales Agent",
    department: "Sales",
    description: "Finds prospects, drafts outreach, and tracks warm replies against your pipeline.",
    ask: "Recommend whether a Sales Agent should be deployed for the Agentic Operating System. Put any deployment under Decisions first.",
    recommended: true,
    skill_ids: ["workspace_data_read", "record_operations", "screen_triage", "document_generation", "failure_diagnosis"],
    required_screens: ["Leads", "Deals Pipeline", "Outreach Queue", "Proposals"],
    starter_tasks: [
      "Rank open leads by likelihood to move and cite the exact row facts.",
      "Draft outreach for warm prospects using approved source context.",
      "Find deals missing next action, owner, value, or follow-up date.",
    ],
    recommended_integrations: ["Gmail", "Workspace sheets", "HubSpot"],
    success_metrics: [
      "Moves sales work from chat into pipeline and outreach screens.",
      "Drafts messages without sending unless the user approves.",
      "Shows missing CRM or email permissions when blocked.",
    ],
  },
  {
    id: "growth_marketer",
    name: "Growth Marketer",
    department: "Marketing",
    description: "Reads campaign performance, spots weak channels, and proposes weekly growth moves.",
    ask: "Recommend whether a Growth Marketer should be deployed for the Agentic Operating System. Put any deployment under Decisions first.",
    recommended: true,
    skill_ids: ["workspace_data_read", "audience_research", "copywriting", "creative_testing", "screen_triage", "outlook_projection", "failure_diagnosis"],
    required_screens: ["Campaigns", "Creative Library", "Content Calendar", "Performance Results", "Brand Kit"],
    starter_tasks: [
      "Build a campaign brief from the best available workspace data.",
      "Recommend which creative asset should be generated next and which model should make it.",
      "Show campaign assets that need approval, reuse, or performance review.",
    ],
    recommended_integrations: ["Workspace files", "Workspace sheets", "Gmail"],
    success_metrics: [
      "Chooses image/video/copy model based on the requested asset.",
      "Shows generated results in chat and saves them to Workspace.",
      "Never generates claims that are not grounded in approved source data.",
    ],
  },
  {
    id: "content_strategist",
    name: "Content Strategist",
    department: "Content",
    description: "Turns company context into audience-backed calendars, angles, and reusable campaign plans.",
    ask: "Recommend whether a Content Strategist should be deployed for the Agentic Operating System. Put any deployment under Decisions first.",
    recommended: true,
    skill_ids: ["workspace_data_read", "audience_research", "copywriting", "creative_testing", "launch_rollout_planning", "document_generation", "failure_diagnosis"],
    required_screens: ["Creative Briefs", "Audience Research", "Message Tests", "Content Calendar", "Creative Library", "Brand Kit"],
    starter_tasks: [
      "Turn workspace context into a content calendar with reusable angles.",
      "Create a creative brief and list the source data still missing.",
      "Recommend which asset should be video, image, copy, or document.",
    ],
    recommended_integrations: ["Workspace files", "Workspace sheets", "Calendar"],
    success_metrics: [
      "Turns repeat content work into briefs, calendars, and saved assets.",
      "Separates source facts from generated creative ideas.",
      "Routes image/video tasks to the correct creative skill.",
    ],
  },
  {
    id: "content_creator",
    name: "Content Creator",
    department: "Content",
    description: "Owns end-to-end creative work: audience research, offer framing, copywriting, content strategy, campaign planning, image briefs, video concepts, shooting scripts, talking-head scripts, UGC angles, visual direction, lighting/grading direction, approval packaging, and performance iteration.",
    ask: "Recommend whether a Content Creator should be deployed for the Agentic Operating System. Put any deployment under Decisions first, with its own creative Studio as the production workspace for research, copy, scripts, image/video briefs, approvals, receipts, and performance iteration.",
    recommended: true,
    studio_enabled: true,
    studio_kind: "content",
    skill_ids: ["workspace_data_read", "audience_research", "copywriting", "creative_testing", "ugc_persona_management", "launch_rollout_planning", "document_generation", "failure_diagnosis"],
    required_screens: ["Audience Research", "Creator Personas", "Creative Briefs", "Message Tests", "UGC Video Tests", "Creative Library", "Content Calendar", "Performance Results", "Brand Kit", "Approval Queue"],
    starter_tasks: [
      "Research the target audience and turn pains, objections, proof, and niche language into messaging angles.",
      "Turn one launch idea into image, video, caption, email, and landing-page copy variants for approval.",
      "Compare performance rows and recommend the next creative or copy test instead of guessing.",
    ],
    recommended_integrations: ["Workspace files", "Workspace sheets"],
    success_metrics: [
      "Uses web research, workspace data, and brand rules before generating public-facing copy or media.",
      "Saves generated media and copy variants as receipts with provider, model, source context, and approval state.",
      "Iterates from real performance data and routes unsupported claims, missing files, and approvals back to the right screen.",
    ],
  },
  {
    id: "ugc_creator_manager",
    name: "UGC Creator Manager",
    department: "Content",
    description: "Creates and manages a bench of 10-12 UGC-style creator personas for launch videos, scripts, hooks, tests, and performance learning.",
    ask: "Recommend whether a UGC Creator Manager should be deployed for the Agentic Operating System. Put any deployment under Decisions first, with a creative Studio for creator personas, UGC scripts, talking-head/video tests, approvals, performance, and receipts.",
    recommended: true,
    studio_enabled: true,
    studio_kind: "content",
    skill_ids: ["workspace_data_read", "audience_research", "copywriting", "creative_testing", "ugc_persona_management", "launch_rollout_planning", "failure_diagnosis"],
    required_screens: ["Creator Personas", "UGC Scripts", "UGC Video Tests", "Creative Library", "Content Calendar", "Performance Results", "Approval Queue", "Brand Kit"],
    starter_tasks: [
      "Create 10-12 distinct creator personas with audience segment, voice, hook style, objections, and launch angle.",
      "Turn the approved rollout message into talking-head scripts, captions, and video prompts for each persona.",
      "Track which UGC variants are approved, generated, published, performing, or blocked by missing proof.",
    ],
    recommended_integrations: ["Workspace files", "Workspace sheets", "TikTok/Meta performance export"],
    success_metrics: [
      "Every creator persona has a clear audience job, message angle, disclosure/approval state, and source facts.",
      "Generated talking-head or UGC videos are saved with provider, model, prompt, cost, approval, and performance receipts.",
      "The next creator batch is chosen from real performance data, not generic content guesses.",
    ],
  },
  {
    id: "social_media_calendar",
    name: "Social Media Calendar Agent",
    department: "Social Media",
    description: "Plans channel calendars, drafts platform-specific posts, tracks approvals, and keeps campaigns moving.",
    ask: "Propose deploying a Social Media Calendar Agent for the Agentic Operating System. Put it under Decisions first, with screens for content calendar, posts, approvals, and performance.",
    recommended: true,
    skill_ids: ["workspace_data_read", "audience_research", "copywriting", "creative_testing", "document_generation", "failure_diagnosis"],
    required_screens: ["Content Calendar", "Social Posts", "Campaign Assets", "Approval Queue", "Performance Results"],
    starter_tasks: [
      "Build this week's posting calendar from approved campaign and brand context.",
      "Show posts waiting for approval, missing creative, or missing publish dates.",
      "Recommend which post should become image, short video, carousel, or email.",
    ],
    recommended_integrations: ["Workspace files", "Workspace sheets", "Calendar"],
    success_metrics: [
      "Keeps social work in calendar, post, asset, and approval screens.",
      "Routes creative generation to the correct image, video, or copy skill.",
      "Separates approved brand facts from generated campaign ideas.",
    ],
  },
  {
    id: "community_engagement",
    name: "Community Engagement Agent",
    department: "Social Media",
    description: "Tracks comments, DMs, response queues, creator mentions, and follow-up opportunities.",
    ask: "Propose deploying a Community Engagement Agent for the Agentic Operating System. Put it under Decisions first, with screens for inbox, mentions, response queue, and engagement opportunities.",
    recommended: true,
    skill_ids: ["workspace_data_read", "screen_triage", "document_generation", "record_operations", "failure_diagnosis"],
    required_screens: ["Engagement Inbox", "Mentions", "Response Queue", "Community Opportunities"],
    starter_tasks: [
      "Rank messages, comments, or mentions that need a response first.",
      "Draft responses using approved brand voice and flag sensitive replies for approval.",
      "Find recurring engagement patterns that should become a reusable workflow.",
    ],
    recommended_integrations: ["Workspace sheets", "Workspace files", "Gmail"],
    success_metrics: [
      "Turns community messages into visible response queues and follow-ups.",
      "Does not publish or send without approval.",
      "Flags missing platform data or permissions before guessing.",
    ],
  },
  {
    id: "copywriter",
    name: "Copywriter",
    department: "Content",
    description: "Drafts posts, emails, landing copy, and newsletters in the user’s actual voice.",
    ask: "Recommend whether a Copywriter should be deployed for the Agentic Operating System. Put any deployment under Decisions first.",
    recommended: true,
    skill_ids: ["workspace_data_read", "audience_research", "copywriting", "creative_testing", "document_generation", "failure_diagnosis"],
    required_screens: ["Audience Research", "Brand Kit", "Copy Drafts", "Message Tests", "Campaign Assets", "Approval Queue"],
    starter_tasks: [
      "Draft campaign copy using only approved source facts and brand voice.",
      "Show copy variants that need approval or performance testing.",
      "Identify missing brand voice rules before writing.",
    ],
    recommended_integrations: ["Workspace files", "Gmail", "Workspace sheets"],
    success_metrics: [
      "Uses Brand Kit rules before drafting public copy.",
      "Stores drafts and approvals in Workspace instead of losing them in chat.",
      "Flags unsupported claims before producing final copy.",
    ],
  },
  {
    id: "oil_gas_field_ops",
    name: "Field Operations Analyst",
    department: "Oil & Gas",
    description: "Watches wells, assets, production checks, maintenance work, downtime, and field handoffs.",
    ask: "Propose deploying a Field Operations Analyst for the Agentic Operating System. Put it under Decisions first, with screens for wells, assets, maintenance, production checks, and downtime.",
    recommended: true,
    skill_ids: ["workspace_data_read", "screen_triage", "outlook_projection", "record_operations", "failure_diagnosis"],
    required_screens: ["Wells", "Assets", "Maintenance Queue", "Production Checks", "Downtime Log"],
    starter_tasks: [
      "Find wells, assets, or field tasks that need attention first and cite the source rows.",
      "Show maintenance items missing owner, due date, status, or last inspection.",
      "Project operational risk from downtime, stale checks, and missing field notes.",
    ],
    recommended_integrations: ["Workspace sheets", "Workspace files"],
    success_metrics: [
      "Ranks field priorities from approved operational data.",
      "Keeps maintenance, production, and downtime work visible in Workspace.",
      "Turns missing SCADA/files/spreadsheets into connector or approved local access requests.",
    ],
  },
  {
    id: "hse_compliance",
    name: "HSE Compliance Agent",
    department: "Oil & Gas",
    description: "Tracks safety actions, permits, incidents, inspections, compliance evidence, and renewal deadlines.",
    ask: "Propose deploying an HSE Compliance Agent for the Agentic Operating System. Put it under Decisions first, with screens for permits, inspections, incidents, evidence, and corrective actions.",
    recommended: true,
    skill_ids: ["workspace_data_read", "screen_triage", "document_generation", "failure_diagnosis"],
    required_screens: ["Permits", "Inspections", "Incidents", "Corrective Actions", "Evidence Vault"],
    starter_tasks: [
      "Find permits, inspections, or corrective actions that are overdue or missing evidence.",
      "Create an HSE attention list with source records, owners, and due dates.",
      "Draft a document request checklist for missing compliance evidence.",
    ],
    recommended_integrations: ["Workspace files", "Workspace sheets", "Calendar"],
    success_metrics: [
      "Keeps safety and compliance work auditable and Decision-first.",
      "Shows missing evidence before claiming compliance readiness.",
      "Routes sensitive operational changes through approval.",
    ],
  },
  {
    id: "finance_controller",
    name: "Finance Controller",
    department: "Finance",
    description: "Tracks cash, invoices, expenses, and payment risks from connected financial data.",
    ask: "Recommend whether a Finance Controller should be deployed for the Agentic Operating System. Put any deployment under Decisions first.",
    recommended: true,
    skill_ids: ["workspace_data_read", "spreadsheet_analysis", "screen_triage", "outlook_projection", "failure_diagnosis"],
    required_screens: ["Finance Dashboard", "Invoices", "Expenses", "Cash Forecast", "Payment Risks"],
    starter_tasks: [
      "Show cash, invoice, and expense risks with the source rows behind them.",
      "Find overdue invoices or payments missing owner, due date, or next action.",
      "Project the next 30 days and state confidence based on available data.",
    ],
    recommended_integrations: ["Workspace sheets", "Stripe", "QuickBooks"],
    success_metrics: [
      "Uses real financial rows or clearly asks for the source system.",
      "Separates verified numbers from projections and assumptions.",
      "Routes sensitive money actions through confirmation or Decisions.",
    ],
  },
  {
    id: "project_manager",
    name: "Project Manager",
    department: "Delivery",
    description: "Watches deadlines, blockers, handoffs, and overdue work across delivery boards.",
    ask: "Recommend whether a Project Manager should be deployed for the Agentic Operating System. Put any deployment under Decisions first.",
    recommended: true,
    skill_ids: ["workspace_data_read", "record_operations", "screen_triage", "outlook_projection", "failure_diagnosis"],
    required_screens: ["Projects", "Tasks", "Blockers", "Handoffs", "Delivery Calendar"],
    starter_tasks: [
      "Find overdue work, blocked handoffs, and projects without a next action.",
      "Create a delivery priority list from current project rows.",
      "Recommend the smallest workflow change that would reduce repeat blockers.",
    ],
    recommended_integrations: ["Workspace sheets", "Workspace files", "Calendar"],
    success_metrics: [
      "Turns blockers into visible records, owners, and due dates.",
      "Keeps delivery actions auditable through Activity.",
      "Escalates missing project data into connector or approved local access requests.",
    ],
  },
  {
    id: "customer_success",
    name: "Customer Success Agent",
    department: "Customer Success",
    description: "Reads tickets, emails, and customer records to surface churn risk and next actions.",
    ask: "Recommend whether a Customer Success Agent should be deployed for the Agentic Operating System. Put any deployment under Decisions first.",
    recommended: true,
    skill_ids: ["workspace_data_read", "screen_triage", "outlook_projection", "document_generation", "failure_diagnosis"],
    required_screens: ["Accounts", "Tickets", "Renewal Risks", "Success Plans"],
    starter_tasks: [
      "Rank accounts by risk and cite ticket, email, or usage evidence.",
      "Draft a success plan for an at-risk account using approved facts.",
      "Find customers missing owner, renewal date, health status, or next action.",
    ],
    recommended_integrations: ["Gmail", "Workspace sheets", "HubSpot"],
    success_metrics: [
      "Shows churn risk with supporting rows and confidence.",
      "Keeps account next steps visible in Workspace screens.",
      "Does not invent customer sentiment when source data is missing.",
    ],
  },
  {
    id: "talent_recruiter",
    name: "Talent Recruiter",
    department: "Recruiting",
    description: "Screens candidates, drafts shortlists, and keeps hiring stages moving.",
    ask: "Recommend whether a Talent Recruiter should be deployed for the Agentic Operating System. Put any deployment under Decisions first.",
    recommended: true,
    skill_ids: ["workspace_data_read", "screen_triage", "document_generation", "failure_diagnosis"],
    required_screens: ["Candidates", "Interview Pipeline", "Scorecards", "Offers"],
    starter_tasks: [
      "Rank candidates for a role using only available candidate evidence.",
      "Find candidates stuck in a stage or missing scorecards.",
      "Draft an interview shortlist and list what data is still missing.",
    ],
    recommended_integrations: ["Workspace files", "Gmail", "Calendar"],
    success_metrics: [
      "Tracks candidate stages and missing evidence without guessing.",
      "Keeps interviews, scorecards, and offers in Workspace.",
      "Routes scheduling and document needs to the right connector.",
    ],
  },
  {
    id: "compliance_admin",
    name: "Compliance Admin",
    department: "Operations",
    description: "Tracks filings, approvals, renewal dates, and documentation gaps.",
    ask: "Recommend whether a Compliance Admin should be deployed for the Agentic Operating System. Put any deployment under Decisions first.",
    skill_ids: ["workspace_data_read", "screen_triage", "document_generation", "failure_diagnosis"],
    required_screens: ["Compliance Calendar", "Filings", "Approvals", "Evidence Vault"],
    starter_tasks: [
      "Find upcoming filings, missing evidence, and approvals without owners.",
      "Create a compliance attention list with due dates and source records.",
      "Draft a document request checklist for missing evidence.",
    ],
    recommended_integrations: ["Workspace files", "Gmail", "Calendar"],
    success_metrics: [
      "Keeps compliance work Decision-first and auditable.",
      "Shows missing documents or permissions before claiming readiness.",
      "Never treats unverified files as approved evidence.",
    ],
  },
  {
    id: "research_agent",
    name: "Research Agent",
    department: "Research",
    description: "Turns files, web context, and internal notes into sharp briefs and comparisons.",
    ask: "Recommend whether a Research Agent should be deployed for the Agentic Operating System. Put any deployment under Decisions first.",
    skill_ids: ["workspace_data_read", "document_generation", "spreadsheet_analysis", "connector_discovery", "failure_diagnosis"],
    required_screens: ["Research Briefs", "Sources", "Comparisons", "Open Questions"],
    starter_tasks: [
      "Turn approved source files into a brief with citations and open questions.",
      "Compare options using the available workspace evidence.",
      "Identify which source, permission, or connector is missing before researching further.",
    ],
    recommended_integrations: ["Workspace files", "Workspace sheets"],
    success_metrics: [
      "Separates sourced facts, analysis, and assumptions.",
      "Saves briefs and comparisons in Workspace for reuse.",
      "Asks for the smallest source permission when blocked.",
    ],
  },
  {
    id: "skill_discovery_researcher",
    name: "Skill Discovery Researcher",
    department: "Research",
    description: "Researches unfamiliar domains, verifies sources, and proposes the exact skills, screens, and agents the Agentic Operating System should add next.",
    ask: "Research the Agentic Operating System context before recommending specialist agents. Find evidence for the user's industry workflow, identify required skills/screens/tools, and stage any proposed new agent or skill file as a Decision with sources.",
    recommended: true,
    skill_ids: ["web_research", "source_verification", "skill_gap_analysis", "screen_blueprint_mapping", "failure_diagnosis"],
    required_screens: ["Research Briefs", "Sources", "Skill Backlog", "Open Questions"],
    starter_tasks: [
      "Research the user's domain and summarize the repeatable workflows with source links.",
      "Map the workflow to required screens, connectors, and skill files before any specialist agent is suggested.",
      "Create a Decision only when the evidence supports a new agent, skill, or screen.",
    ],
    recommended_integrations: ["Web research", "Workspace files"],
    success_metrics: [
      "Every proposed skill or agent cites evidence and explains why existing skills are insufficient.",
      "Unknown domains produce research briefs first, not speculative specialist agents.",
      "New capabilities go through Decisions with security, permission, and data-source requirements.",
    ],
  },
  {
    id: "partnerships_agent",
    name: "Partnerships Agent",
    department: "Partnerships",
    description: "Tracks partner conversations, follow-ups, obligations, and expansion opportunities.",
    ask: "Recommend whether a Partnerships Agent should be deployed for the Agentic Operating System. Put any deployment under Decisions first.",
    recommended: true,
    skill_ids: ["workspace_data_read", "record_operations", "screen_triage", "outlook_projection", "failure_diagnosis"],
    required_screens: ["Partners", "Partner Pipeline", "Obligations", "Expansion Opportunities"],
    starter_tasks: [
      "Rank partner conversations by next action and expansion potential.",
      "Find obligations, follow-ups, or renewal moments without owners.",
      "Recommend which partnership needs attention this week and why.",
    ],
    recommended_integrations: ["Gmail", "Workspace sheets", "Workspace files"],
    success_metrics: [
      "Turns partner commitments into visible obligations and follow-ups.",
      "Uses real conversation or record evidence before recommending expansion.",
      "Records partner actions and proposals in Activity or Decisions.",
    ],
  },
];

// STAGE 1 — Agent Library lockdown. The full LIBRARY_AGENTS array stays intact
// (deploy-by-id + already-deployed agents still resolve); this allowlist gates
// only the user-facing surfacing points (catalog grid, "recommended next",
// research fallback, department tabs).
const ALLOWED_LIBRARY_AGENT_IDS = new Set(["finance_controller", "sdr", "growth_marketer"]);
// Display order for the locked-down Recommended tab: Sales, Marketing, Finance.
const ALLOWED_LIBRARY_AGENT_ORDER = ["sdr", "growth_marketer", "finance_controller"];
const LIBRARY_DEPARTMENTS = Array.from(
  new Set(
    LIBRARY_AGENTS
      .filter((agent) => ALLOWED_LIBRARY_AGENT_IDS.has(agent.id))
      .map((agent) => agent.department),
  ),
);
const UNIVERSAL_LIBRARY_DEPARTMENTS = new Set(["Code"]);
const UNIVERSAL_RECOMMENDED_DEPARTMENTS = new Set(["Code"]);
const PRIMARY_DEPARTMENT_MATCH_THRESHOLD = 1.75;
const RELATED_DEPARTMENT_MATCH_THRESHOLD = 3;

const CONTEXT_DEPARTMENT_KEYWORDS: Record<string, string[]> = {
  Code: [
    "code",
    "coding",
    "coder",
    "developer",
    "website",
    "web site",
    "landing page",
    "site",
    "repo",
    "repository",
    "github",
    "app screen",
    "frontend",
    "backend",
    "component",
    "html",
    "css",
    "react",
    "vite",
    "next.js",
    "modify website",
    "edit website",
    "build website",
  ],
  Launch: [
    "launch",
    "launch initiatives",
    "product readiness",
    "demo",
    "pilot",
    "bug",
    "bug fixes",
    "user feedback",
    "deployment",
    "deployments",
    "founder decision",
    "founder decisions",
    "readiness scorecard",
    "readiness",
    "rollout",
    "rollout plan",
    "go to market",
    "gtm",
    "product",
    "startup",
    "saas",
    "agentic",
    "scaling sloe",
    "scaling sloe lab",
    "scaling sloe labs",
    "scaling sloe slab",
    "scaling sloe slabs",
  ],
  Football: [
    "fc",
    "football",
    "soccer",
    "player",
    "players",
    "roster",
    "scouting",
    "scout",
    "watchlist",
    "squad",
    "match",
    "training",
    "onze",
    "jogador",
    "jogadores",
  ],
  Performance: ["football", "soccer", "player", "players", "training", "development", "performance", "risk signals"],
  "Real Estate": [
    "real estate",
    "realty",
    "brokerage",
    "off plan",
    "off-plan",
    "listing",
    "listings",
    "property",
    "properties",
    "buyer",
    "buyers",
    "seller",
    "sellers",
    "tenant",
    "tenants",
    "landlord",
    "landlords",
    "viewing",
    "viewings",
  ],
  Sales: ["lead", "leads", "deal", "deals", "pipeline", "prospect", "prospects", "proposal", "proposals"],
  Marketing: ["campaign", "campaigns", "creative", "creative asset", "creative assets", "content calendar", "brand kit", "growth", "ad", "ads"],
  Content: [
    "content",
    "content creator",
    "copy",
    "newsletter",
    "post",
    "posts",
    "creative brief",
    "creative briefs",
    "creative library",
    "brand kit",
    "ugc",
    "ugc creator",
    "creator profile",
    "creator profiles",
    "creator persona",
    "creator personas",
    "talking head",
    "avatar",
    "ai avatar",
    "virtual influencer",
    "campaign pack",
    "launch asset",
    "launch assets",
  ],
  "Social Media": [
    "social media",
    "social",
    "instagram",
    "tiktok",
    "meta ads",
    "facebook ads",
    "linkedin",
    "youtube",
    "reels",
    "shorts",
    "posts",
    "content calendar",
    "engagement",
    "community",
    "creator",
    "influencer",
    "ugc",
    "creator persona",
    "talking head",
    "campaign assets",
  ],
  "Oil & Gas": [
    "oil and gas",
    "oil & gas",
    "energy",
    "petroleum",
    "upstream",
    "midstream",
    "downstream",
    "well",
    "wells",
    "rig",
    "rigs",
    "field ops",
    "field operations",
    "production checks",
    "downtime",
    "hse",
    "permit",
    "permits",
    "inspection",
    "inspections",
    "maintenance queue",
  ],
  Finance: ["invoice", "invoices", "expense", "expenses", "cash", "payment", "payments", "stripe", "quickbooks"],
  Delivery: ["project", "projects", "task", "tasks", "blocker", "blockers", "delivery", "handoff"],
  "Customer Success": ["account", "accounts", "ticket", "tickets", "renewal", "renewals", "churn", "success plan"],
  Recruiting: ["candidate", "candidates", "interview", "interviews", "scorecard", "scorecards", "offer", "offers"],
  Research: ["research", "source", "sources", "brief", "briefs", "comparison", "comparisons"],
  Partnerships: ["partner", "partners", "partnership", "partnerships", "obligation", "obligations"],
};

type DepartmentMatchSignal = {
  score: number;
  matches: string[];
};

function normalizeContextText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function contextHasKeyword(contextText: string, keyword: string): boolean {
  const normalized = normalizeContextText(keyword).trim();
  if (!normalized) return false;
  const escaped = normalized.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (normalized.includes(" ")) {
    return new RegExp(`\\b${escaped.replace(/\\s+/g, "\\s+")}\\b`).test(contextText);
  }
  return new RegExp(`(^|\\s)${escaped}($|\\s)`).test(contextText);
}

function keywordWeight(keyword: string): number {
  const normalized = normalizeContextText(keyword);
  const words = normalized.split(" ").filter(Boolean);
  const baseWeight = words.length > 1 ? 1.2 : 1;
  const bonus = Math.max(0, Math.min(1, normalized.length / 16));
  return baseWeight + bonus;
}

function getDepartmentSignals(contextText: string): Map<string, DepartmentMatchSignal> {
  const signals = new Map<string, DepartmentMatchSignal>();
  const normalizedContextText = normalizeContextText(contextText);

  for (const [department, keywords] of Object.entries(CONTEXT_DEPARTMENT_KEYWORDS)) {
    let score = 0;
    const matches: string[] = [];

    for (const keyword of keywords) {
      if (!contextHasKeyword(normalizedContextText, keyword)) continue;
      score += keywordWeight(keyword);
      matches.push(keyword);
    }

    if (score > 0) {
      signals.set(department, { score, matches: Array.from(new Set(matches)) });
    }
  }

  return signals;
}

function inferDepartmentsFromSignals(signals: Map<string, DepartmentMatchSignal>): Set<string> {
  const departments = new Set<string>();

  for (const [department, signal] of signals) {
    if (signal.score >= PRIMARY_DEPARTMENT_MATCH_THRESHOLD) {
      departments.add(department);
    }
  }

  return departments;
}

function inferDepartmentContext(osName: string, workspaceScreens?: WorkspacePulseScreen[]) {
  const contextText = normalizeContextText([
    osName,
    ...(workspaceScreens || []).flatMap((screen) => [screen.screen_id, screen.label]),
  ].filter(Boolean).join(" "));

  const signals = getDepartmentSignals(contextText);
  const departments = inferDepartmentsFromSignals(signals);
  const isConfidentIn = (department: string) => {
    return (signals.get(department)?.score ?? 0) >= RELATED_DEPARTMENT_MATCH_THRESHOLD;
  };

  if (departments.has("Football")) {
    if (isConfidentIn("Football")) {
      departments.add("Performance");
      departments.add("Operations");
    }
  }
  if (departments.has("Real Estate")) {
    if (isConfidentIn("Real Estate")) {
      departments.add("Sales");
      departments.add("Marketing");
      departments.add("Finance");
    }
  }
  if (departments.has("Social Media")) {
    if (isConfidentIn("Social Media")) {
      departments.add("Content");
      departments.add("Marketing");
    }
  }
  if (departments.has("Oil & Gas")) {
    if (isConfidentIn("Oil & Gas")) {
      departments.add("Operations");
    }
  }
  if (departments.has("Launch")) {
    if (isConfidentIn("Launch")) {
      departments.add("Marketing");
      departments.add("Content");
      departments.add("Sales");
      departments.add("Delivery");
      departments.add("Customer Success");
    }
  }
  if (departments.has("Sales") && (signals.get("Sales")?.score ?? 0) >= RELATED_DEPARTMENT_MATCH_THRESHOLD) departments.add("Marketing");
  if (departments.has("Recruiting")) departments.add("Operations");

  const hasPrimarySignal = departments.size > 0;

  if (!hasPrimarySignal) {
    UNIVERSAL_RECOMMENDED_DEPARTMENTS.forEach((department) => departments.add(department));
  }
  departments.add("Code");

  return { departments, hasPrimarySignal };
}

export function inferRecommendedDepartments(osName: string, workspaceScreens?: WorkspacePulseScreen[]): Set<string> {
  return inferDepartmentContext(osName, workspaceScreens).departments;
}

export function visibleLibraryDepartmentsForContext(
  osName: string,
  workspaceScreens?: WorkspacePulseScreen[],
  activeDepartments?: Set<string>,
): string[] {
  const departments = inferRecommendedDepartments(osName, workspaceScreens);
  const visible = new Set<string>([...UNIVERSAL_LIBRARY_DEPARTMENTS, ...departments]);
  (activeDepartments || new Set<string>()).forEach((department) => {
    if (department) visible.add(department);
  });

  if (visible.size === 0) visible.add("Research");
  return LIBRARY_DEPARTMENTS.filter((department) => visible.has(department));
}

function recommendationReason(agent: LibraryAgent, departments: Set<string>, osName: string, workspaceScreens?: WorkspacePulseScreen[]): string {
  const screenLabels = (workspaceScreens || []).map((screen) => screen.label || humanize(screen.screen_id));
  const screenText = screenLabels.slice(0, 2).join(", ");
  const base = screenText ? `Matches ${screenText}` : `Matches ${osName}`;

  if (agent.department === "Football") return `${base}: player, scouting, and roster work belong in the Agentic Operating System.`;
  if (agent.department === "Performance") return `${base}: player progress and risk signals need specialist attention.`;
  if (agent.department === "Operations" && departments.has("Football")) {
    return "Recommended support staff for contracts, documents, renewals, and follow-ups around the football operation.";
  }
  if (agent.department === "Real Estate") return `${base}: listings, viewings, offers, and property follow-ups are active operating data.`;
  if (agent.department === "Sales") return `${base}: pipeline movement, follow-ups, and proposals need a sales specialist.`;
  if (agent.department === "Marketing" || agent.department === "Content") {
    return `${base}: campaign, creative, or brand work should have its own workspace and approval flow.`;
  }
  if (agent.department === "Social Media") {
    return `${base}: posts, channels, creative assets, approvals, and engagement need social-specific staff.`;
  }
  if (agent.department === "Oil & Gas") {
    return `${base}: wells, field operations, HSE, maintenance, or production checks need industry-specific oversight.`;
  }
  if (agent.department === "Launch") {
    return `${base}: launch initiatives, proof gates, demos, bugs, feedback, and founder decisions need one command-center agent.`;
  }
  if (agent.department === "Code") {
    return `${base}: every Agentic Operating System eventually needs website, app, landing page, or code-bundle changes with preview and verification.`;
  }
  if (agent.department === "Finance") return `${base}: money, invoices, payments, and forecasts need stricter receipts and approvals.`;
  if (agent.department === "Delivery") return `${base}: deadlines, blockers, and handoffs should be tracked by delivery staff.`;
  if (agent.department === "Customer Success") return `${base}: accounts, tickets, and renewal risk need dedicated triage.`;
  if (agent.department === "Recruiting") return `${base}: candidates, interviews, and offers need a hiring workflow.`;
  if (agent.department === "Research") return `${base}: sources, briefs, and comparisons need citation-aware work.`;
  if (agent.department === "Partnerships") return `${base}: partners, obligations, and expansion opportunities need follow-up tracking.`;

  return "Recommended because the Agentic Operating System context matches an existing, evidence-backed Sloe agent contract.";
}

function researchRequiredMatches(osName: string, workspaceScreens?: WorkspacePulseScreen[]): RecommendedAgentMatch[] {
  const researchAgents = LIBRARY_AGENTS.filter(
    (agent) =>
      ALLOWED_LIBRARY_AGENT_IDS.has(agent.id) &&
      (agent.id === "skill_discovery_researcher" || agent.id === "research_agent"),
  );
  const visibleContext = [
    osName,
    ...(workspaceScreens || []).map((screen) => screen.label || humanize(screen.screen_id)),
  ].filter(Boolean).slice(0, 4).join(", ");

  return researchAgents.map((agent) => ({
    agent,
    evidenceState: "research_required",
    reason: `Research required before recommending specialist agents. Current context (${visibleContext || "not enough workspace detail"}) does not yet match a proven Sloe vertical contract, so this agent should gather sources, map workflows to skills/screens, and stage any new capability as a Decision.`,
  }));
}

export function recommendedAgentMatchesForContext(osName: string, workspaceScreens?: WorkspacePulseScreen[]): RecommendedAgentMatch[] {
  const inference = inferDepartmentContext(osName, workspaceScreens);
  const departments = inference.departments;

  if (!inference.hasPrimarySignal) {
    const universalRecommended: RecommendedAgentMatch[] = LIBRARY_AGENTS
      .filter((agent) => ALLOWED_LIBRARY_AGENT_IDS.has(agent.id))
      .filter((agent) => UNIVERSAL_RECOMMENDED_DEPARTMENTS.has(agent.department))
      .filter((agent) => agent.recommended)
      .map((agent) => ({
        agent,
        evidenceState: "known_context" as const,
        reason: recommendationReason(agent, departments, osName, workspaceScreens),
      }));

    return [...researchRequiredMatches(osName, workspaceScreens), ...universalRecommended];
  }

  if (departments.size === 0) {
    return researchRequiredMatches(osName, workspaceScreens);
  }

  const agents = LIBRARY_AGENTS.filter((agent) => {
    if (!ALLOWED_LIBRARY_AGENT_IDS.has(agent.id)) return false;
    if (!departments.has(agent.department)) return false;
    return agent.recommended;
  });

  if (agents.length === 0) {
    return researchRequiredMatches(osName, workspaceScreens);
  }

  return agents.map((agent) => ({
    agent,
    evidenceState: "known_context" as const,
    reason: recommendationReason(agent, departments, osName, workspaceScreens),
  }));
}

export function recommendedAgentsForContext(osName: string, workspaceScreens?: WorkspacePulseScreen[]): LibraryAgent[] {
  return recommendedAgentMatchesForContext(osName, workspaceScreens).map((match) => match.agent);
}

function recommendationReasonForAgent(agent: LibraryAgent, matches: RecommendedAgentMatch[]): string | null {
  return matches.find((match) => match.agent.id === agent.id)?.reason || null;
}

function recommendationMatchForAgent(agent: LibraryAgent, matches: RecommendedAgentMatch[]): RecommendedAgentMatch | null {
  return matches.find((match) => match.agent.id === agent.id) || null;
}

function recommendationDepartmentSummary(matches: RecommendedAgentMatch[]): string {
  if (matches.length > 0 && matches.every((match) => match.evidenceState === "research_required")) {
    return "Research required before specialist recommendations";
  }
  const departments = Array.from(new Set(matches.map((match) => match.agent.department)));
  if (departments.length === 0) {
    return "No dominant vertical detected yet";
  }
  return departments.slice(0, 3).join(", ");
}

export function buildLaunchReadinessItems(input: LaunchReadinessInput): LaunchReadinessItem[] {
  const items: LaunchReadinessItem[] = [];

  items.push({
    id: "connectors",
    title: input.connectionCount > 0 && input.connectorRepairCount === 0 ? "Live data connected" : "Live data needs attention",
    detail: input.connectionCount === 0
      ? "Connect at least one source of operating data so agents can work from facts instead of asking every time."
      : input.connectorRepairCount > 0
        ? `${input.connectorRepairCount} connection${input.connectorRepairCount === 1 ? "" : "s"} need repair, expanded scopes, or reconnect before agents can trust them.`
        : `${input.connectionCount} connection${input.connectionCount === 1 ? "" : "s"} ready for approved agent work.`,
    status: input.connectionCount > 0 && input.connectorRepairCount === 0 ? "ready" : "needs_action",
    action: input.connectionCount > 0 && input.connectorRepairCount === 0 ? null : "integrations",
    cta: input.connectionCount > 0 && input.connectorRepairCount === 0 ? null : "Open integrations",
    action_detail: input.connectionCount > 0 && input.connectorRepairCount === 0 ? null : {
      why: "Agents need at least one trusted operating data source before they can work from facts instead of repeated questions.",
      involves: "Integration Hub, connector health, OAuth/API scopes, and repair states for broken or missing live data.",
      effect: "Architect opens Integrations and explains what connector or permission is missing before any connection flow starts.",
      approval: "No connector is authorized from this card. The user still approves provider permissions in the connector flow.",
    },
  });

  items.push({
    id: "workspace_data",
    title: input.screenCount > 0 && input.rowCount > 0 ? "Workspace has live rows" : "Workspace still needs data",
    detail: input.screenCount === 0
      ? "This Agentic Operating System needs its workspace screens attached before the agents can save repeatable work."
      : input.rowCount === 0
        ? `${input.screenCount} screen${input.screenCount === 1 ? "" : "s"} exist, but no operating rows have landed yet. Sync or create the first records.`
        : `${input.rowCount} row${input.rowCount === 1 ? "" : "s"} across ${input.screenCount} workspace screen${input.screenCount === 1 ? "" : "s"}.`,
    status: input.screenCount > 0 && input.rowCount > 0 ? "ready" : "needs_action",
    action: input.screenCount > 0 && input.rowCount > 0 ? null : "operator",
    cta: input.screenCount > 0 && input.rowCount > 0 ? null : "Ask Operator",
    action_detail: input.screenCount > 0 && input.rowCount > 0 ? null : {
      why: "The Agentic Operating System needs real rows before Operator, Analyst, and specialist agents can produce useful answers, tables, charts, or actions.",
      involves: "Operator, existing workspace screens, and Integrations for approved data access.",
      effect: "Operator asks where the first source lives and recommends the smallest approved connection path.",
      approval: "No import happens from this card. Any connector or sensitive data move still requires user approval.",
    },
  });

  items.push({
    id: "decisions",
    title: input.pendingDecisionCount === 0 ? "No blocked Decisions" : "Decisions waiting for approval",
    detail: input.pendingDecisionCount === 0
      ? "Structural updates, staff changes, and risky moves are clear right now."
      : `${input.pendingDecisionCount} Decision${input.pendingDecisionCount === 1 ? "" : "s"} need review before the Agentic Operating System can apply those changes.`,
    status: input.pendingDecisionCount === 0 ? "ready" : "needs_action",
    action: input.pendingDecisionCount === 0 ? null : "decisions",
    cta: input.pendingDecisionCount === 0 ? null : "Review Decisions",
    action_detail: input.pendingDecisionCount === 0 ? null : {
      why: "Decisions are the approval boundary for structural changes, new agents, schema upgrades, and risky moves.",
      involves: "The Architect's Evolution screen and the pending Decision cards waiting for review.",
      effect: "The Agentic Operating System opens Evolution so the user can approve, dismiss, or inspect each pending Decision.",
      approval: "Nothing is applied by opening this view. The user still clicks approve on each individual Decision.",
    },
  });

  items.push({
    id: "screen_standard",
    title: input.schemaStandardReady ? "Screen standard current" : "Screen standard needs review",
    detail: input.schemaStandardReady
      ? "This Agentic Operating System is on the current screen standard."
      : input.needsCodeBundleStandardReview
        ? "This code-bundle Agentic Operating System needs Architect to prepare a bundle-safe update plan before it can inherit the latest standard."
        : input.canRequestSchemaStandardUpgrade
          ? "A newer screen standard is available. Stage it as a Decision so existing data is preserved."
          : "The Agentic Operating System needs a schema or bundle review before standard upgrades can be verified.",
    status: input.schemaStandardReady ? "ready" : "needs_action",
    action: input.schemaStandardReady ? null : "schema",
    cta: input.schemaStandardReady ? null : "Review standard",
    action_detail: input.schemaStandardReady ? null : {
      why: "Older Agentic Operating System screens can miss the latest shell improvements, agent workspace cards, receipts, and proactive CTAs.",
      involves: input.needsCodeBundleStandardReview
        ? "Architect, the generated code bundle, current domain screens, and a bundle-safe update plan."
        : "Architect, the current schema standard, existing screens, and a schema upgrade Decision.",
      effect: input.needsCodeBundleStandardReview
        ? "Mission Control expands the bundle update path before any Architect handoff."
        : "Architect stages a schema-standard upgrade Decision; it is not applied until approved.",
      approval: "Existing data and screens are preserved. Structural changes still require Decision approval or a reviewed bundle plan.",
    },
  });

  items.push({
    id: "receipts",
    title: input.receiptCount > 0 ? "Receipts are active" : "Receipts will appear after first action",
    detail: input.receiptCount > 0
      ? `${input.receiptCount} recent action${input.receiptCount === 1 ? "" : "s"} recorded for auditability.`
      : "The audit trail is ready, but no agent action has landed yet.",
    status: input.receiptCount > 0 ? "ready" : "watching",
    action: null,
    cta: null,
    action_detail: null,
  });

  return items;
}

function SectionHeader({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-[11px] font-medium text-white/40 uppercase tracking-[0.12em]">{children}</h2>
      {right}
    </div>
  );
}

function Shimmer({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl bg-white/[0.05] ${className}`} />;
}

function connectorProviderName(provider: string): string {
  return provider
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

// Mount fade-in + upward drift, staggered by `delay`. Pure CSS transition (no
// tailwind keyframe config needed); fires once on mount and stays settled across
// data refreshes (only the skeleton swaps in/out beneath it).
function FadeIn({ delay = 0, children, className }: { delay?: number; children: ReactNode; className?: string }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(r);
  }, []);
  return (
    <div
      className={`transition-all duration-500 ease-out ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      } ${className || ""}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export function MissionControl({
  os,
  userFirstName,
  brandColor,
  workspaceScreens,
  schemaStandardVersion,
  schemaScreenCount,
  schemaBundleType,
  onNavigateAssistant,
  openAgentLibraryIntent,
}: {
  os: { id: string; name?: string };
  userFirstName?: string;
  brandColor?: string;
  workspaceScreens?: WorkspacePulseScreen[];
  schemaStandardVersion?: string | null;
  schemaScreenCount?: number;
  schemaBundleType?: string | null;
  onNavigateAssistant: (assistantId: string, target?: AssistantNavTarget) => void;
  openAgentLibraryIntent?: { nonce: number; department?: string } | null;
}) {
  const accent = brandColor || DEFAULT_BRAND_COLOR;
  const osId = os.id;
  const osName = os.name || "Your Agentic Operating System";

  const [decisions, setDecisions] = useState<Proposal[] | null>(null);
  const [pulse, setPulse] = useState<Array<{ screen_id: string; label?: string; count: number }> | null>(null);
  const [staff, setStaff] = useState<Record<string, ActionRow | null> | null>(null);
  const [staffThreads, setStaffThreads] = useState<Record<string, StaffThreadSignal | null> | null>(null);
  const [receipts, setReceipts] = useState<ActionRow[] | null>(null);
  const [deployedAgents, setDeployedAgents] = useState<DeployedAgent[] | null>(null);
  const [connections, setConnections] = useState<IntegrationConnection[] | null>(null);
  const [connectorHealth, setConnectorHealth] = useState<ConnectorHealthSummary | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryExpanded, setLibraryExpanded] = useState(false);
  const [libraryDepartment, setLibraryDepartment] = useState<string>("Recommended");
  const [selectedLibraryAgent, setSelectedLibraryAgent] = useState<LibraryAgent | null>(null);
  const [requestingAgentId, setRequestingAgentId] = useState<string | null>(null);
  const [requestingSchemaUpgrade, setRequestingSchemaUpgrade] = useState(false);
  const [codeBundleDetailsOpen, setCodeBundleDetailsOpen] = useState(false);
  const [expandedReadinessActionId, setExpandedReadinessActionId] = useState<string | null>(null);
  const [requestAgentFeedback, setRequestAgentFeedback] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  // Bumping this re-runs every read; the header refresh control resets the
  // sections to null first so the designed skeletons shimmer back in.
  const [reloadNonce, setReloadNonce] = useState(0);

  const libraryContainerRef = useRef<HTMLDivElement>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openAgentLibraryIntent) return;
    setLibraryDepartment(openAgentLibraryIntent.department || "Recommended");
    setSelectedLibraryAgent(null);
    setLibraryOpen(true);
  }, [openAgentLibraryIntent?.nonce]);

  useEffect(() => {
    if (libraryOpen && libraryContainerRef.current) {
      libraryContainerRef.current.scrollTop = 0;
    } else if (!libraryOpen && mainContainerRef.current) {
      mainContainerRef.current.scrollTop = 0;
    }
  }, [libraryOpen]);

  const now = new Date();
  const greeting = greetingFor(now);
  const todayStr = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const refreshing = decisions === null || pulse === null || staff === null || staffThreads === null || receipts === null || connections === null || connectorHealth === null;
  const activeStaffCount = ASSISTANT_PROFILES.filter((p) => !isAssistantHiddenFromNav(p.id)).length + (deployedAgents?.length || 0);
  const isCodeBundle = schemaBundleType === "code";
  const hasLiveScreens = typeof schemaScreenCount === "number" ? schemaScreenCount > 0 : true;
  const schemaStandardReady = Boolean(schemaStandardVersion && schemaStandardVersion >= CURRENT_SCHEMA_STANDARD_VERSION);
  const canRequestSchemaStandardUpgrade = hasLiveScreens && !isCodeBundle && !schemaStandardReady;
  const needsCodeBundleStandardReview = isCodeBundle && !schemaStandardReady;
  const deployedAgentIds = new Set(
    (deployedAgents || [])
      .map((agent) => agent.schema_diff?.agent_id)
      .filter(Boolean) as string[],
  );
  const pendingAgentIds = new Set(
    (decisions || [])
      .filter((decision) => decision.type === "agent_deployment")
      .map((decision) => decision.schema_diff?.agent_id)
      .filter(Boolean) as string[],
  );
  const activeLibraryDepartments = new Set(
    [
      ...(deployedAgents || []).map((agent) => agent.schema_diff?.department),
      ...(decisions || [])
        .filter((decision) => decision.type === "agent_deployment")
        .map((decision) => decision.schema_diff?.department),
    ].filter(Boolean) as string[],
  );
  const pendingSchemaUpgrade = (decisions || []).some((decision) => decision.type === "schema_standard_upgrade");
  const contextualRecommendedMatches = recommendedAgentMatchesForContext(osName, workspaceScreens);
  const contextualRecommendedAgents = contextualRecommendedMatches.map((match) => match.agent);
  const visibleLibraryDepartments = visibleLibraryDepartmentsForContext(osName, workspaceScreens, activeLibraryDepartments);
  const recommendedDepartmentsText = recommendationDepartmentSummary(contextualRecommendedMatches);
  // STAGE 1 lockdown — the three allowed specialists ARE the product, not
  // contextual suggestions: always surface them in the Recommended tab so it is
  // never empty, in fixed display order (Sales, Marketing, Finance). Reuse the
  // contextual reason/evidence when present, else a sensible default.
  const guidedAgentMatches: RecommendedAgentMatch[] = ALLOWED_LIBRARY_AGENT_ORDER
    .map((id) => LIBRARY_AGENTS.find((agent) => agent.id === id))
    .filter((agent): agent is LibraryAgent => !!agent)
    .map((agent) => {
      const contextMatch = contextualRecommendedMatches.find((match) => match.agent.id === agent.id);
      return contextMatch || {
        agent,
        evidenceState: "known_context" as const,
        reason: `${agent.name} is one of your core ${agent.department} specialists.`,
      };
    });
  const guidedAgentIds = new Set(guidedAgentMatches.map((match) => match.agent.id));
  const pendingAgentRequests: PendingAgentRequest[] = ((decisions || []).filter((decision) => decision.type === "agent_deployment")).map((decision) => {
    const decisionAgentId = String(decision.schema_diff?.agent_id || "").trim();
    const matchedAgent = decisionAgentId ? LIBRARY_AGENTS.find((agent) => agent.id === decisionAgentId) || null : null;
    return {
      decisionId: decision.id,
      agent: matchedAgent,
      name: matchedAgent?.name || String(decision.schema_diff?.agent_name || decision.title.replace(/^(Deploy|Start)\s+/i, "") || "Requested agent"),
      department: matchedAgent?.department || String(decision.schema_diff?.department || "Specialist"),
      createdAt: decision.created_at,
    };
  });
  const connectorRepairCount = connectorHealth?.repair_needed_count ?? (connections || []).filter((connection) => connectorRepairState(connection).needsRepair).length;
  const workspaceRowCount = (pulse || []).reduce((sum, screen) => sum + screen.count, 0);
  const launchReadinessItems = buildLaunchReadinessItems({
    connectionCount: connections?.length || 0,
    connectorRepairCount,
    screenCount: typeof schemaScreenCount === "number" ? schemaScreenCount : (workspaceScreens?.length || 0),
    rowCount: workspaceRowCount,
    pendingDecisionCount: decisions?.length || 0,
    receiptCount: receipts?.length || 0,
    schemaStandardReady,
    canRequestSchemaStandardUpgrade,
    needsCodeBundleStandardReview,
  });
  const launchReadyCount = launchReadinessItems.filter((item) => item.status === "ready").length;
  const launchNeedsActionCount = launchReadinessItems.filter((item) => item.status === "needs_action").length;
  const launchChecklistLoading = connections === null || connectorHealth === null || pulse === null || decisions === null || receipts === null;

  const refresh = () => {
    setDecisions(null);
    setPulse(null);
    setStaff(null);
    setStaffThreads(null);
    setReceipts(null);
    setDeployedAgents(null);
    setConnections(null);
    setConnectorHealth(null);
    setReloadNonce((n) => n + 1);
  };

  // The system (president) assistant owns evolution — Review handoffs target it.
  const systemProfile = ASSISTANT_PROFILES.find((p) => p.is_system);
  // The Operator owns weekly focus — Needs Attention deep-links to its chat.
  const operatorProfile = ASSISTANT_PROFILES.find((p) => p.id === "operator");

  const requestAgent = async (agent: LibraryAgent) => {
    if (!systemProfile) return;
    if (deployedAgentIds.has(agent.id) || pendingAgentIds.has(agent.id)) return;
    const isResearchGate = agent.id === "skill_discovery_researcher" || agent.id === "research_agent";
    setRequestingAgentId(agent.id);
    setRequestAgentFeedback(null);
    const title = clipProposalText(isResearchGate ? `Start ${agent.name}` : `Deploy ${agent.name}`, 80);
    const description = clipProposalText(
      isResearchGate
        ? `${agent.name} would gather sources, map the user's niche workflow to screens and skill files, and return any new capability as a separate Decision before activation.`
        : `${agent.name} would join the Agentic Operating System as specialist staff for ${agent.department.toLowerCase()} work. Approval records the request before anything is activated.`,
      1000,
    );
    const rationale = clipProposalText(
      isResearchGate
        ? `${agent.description} The current Agentic Operating System context needs evidence before Sloe recommends a specialist agent, so this request starts research and verification first.`
        : `${agent.description} The user requested it from the Agent Library, so it must be staged as a Decision before deployment.`,
      500,
    );
    const schemaDiff = {
      agent_id: agent.id,
      agent_name: agent.name,
      department: agent.department,
      requested_access: isResearchGate ? "public research sources, workspace context, and approved integrations only" : "workspace data and approved integrations only",
      evidence_state: isResearchGate ? "research_required" : "known_context",
      skill_ids: withUniversalAgentSkills(agent.skill_ids || ["workspace_data_read", "screen_triage", "outlook_projection", "failure_diagnosis"]),
      studio_enabled: agent.studio_enabled === true,
      studio_kind: agent.studio_kind,
      required_screens: agent.required_screens || [],
      starter_tasks: agent.starter_tasks || [],
      recommended_integrations: filterLocalAccessReferences(agent.recommended_integrations || []),
      success_metrics: agent.success_metrics || [
        "Uses real workspace data or clearly explains what is missing.",
        "Shows results in chat and saves repeatable work to Workspace.",
        "Turns missing screens, tools, or permissions into the next unblock step.",
      ],
      successful_run_definition: isResearchGate
        ? "The researcher cites sources or approved user evidence, maps the repeated job-to-be-done to existing skills first, names missing screens/tools/permissions, and stages any new capability as a Decision with success criteria."
        : "The agent completes the requested work using approved data, records the output, and explains missing data, permission, tool, or skill blockers if it cannot finish.",
      telemetry_plan: isResearchGate ? {
        events: [
          "auto_research_requested",
          "auto_research_sources_collected",
          "auto_research_synthesis_completed",
          "auto_research_decision_proposed",
          "auto_research_failed",
        ],
        successful_run_definition: "The researcher cites sources or approved user evidence, maps the repeated job-to-be-done to existing skills first, names missing screens/tools/permissions, and stages any new capability as a Decision with success criteria.",
        failure_categories: [
          "missing_sources",
          "weak_evidence",
          "no_repeated_job_to_be_done",
          "existing_skill_sufficient",
          "permission_or_connector_missing",
          "security_or_compliance_risk",
        ],
        receipt_requirements: [
          "Record which sources were used or why sources are still missing.",
          "Record the skill gaps considered and existing skills ruled in or out.",
          "Record every proposed agent, screen, connector, or skill as a separate Decision.",
        ],
      } : null,
    };
    try {
      const res = await fetch("/api/evolution/proposals", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspace_id: osId,
          type: "agent_deployment",
          title,
          description,
          rationale,
          schema_diff: schemaDiff,
          proposed_by: "architect",
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || json?.ok !== true) throw new Error(json?.error || `HTTP ${res.status}`);
      const optimisticDecision: Proposal = {
        id: String(json.proposal_id || `local-${agent.id}-${Date.now()}`),
        type: "agent_deployment",
        title,
        rationale,
        created_at: new Date().toISOString(),
        schema_diff: schemaDiff,
      };
      setDecisions((current) => [
        optimisticDecision,
        ...((current || []).filter((decision) => decision.schema_diff?.agent_id !== agent.id)),
      ]);
      setRequestAgentFeedback({
        kind: "success",
        text: `${agent.name} requested. Review and approve it in Decisions before it joins the sidebar.`,
      });
      openLibraryAgent(agent);
      setReloadNonce((n) => n + 1);
    } catch (err: any) {
      setRequestAgentFeedback({
        kind: "error",
        text: `Could not request ${agent.name}: ${err?.message || "unknown error"}`,
      });
    } finally {
      setRequestingAgentId(null);
    }
  };

  const openLibraryAgent = (agent: LibraryAgent) => {
    const isContextualRecommendation = contextualRecommendedAgents.some((recommended) => recommended.id === agent.id);
    setLibraryDepartment(isContextualRecommendation ? "Recommended" : agent.department);
    setSelectedLibraryAgent(agent);
    setLibraryOpen(true);
  };

  const openDeployedAgent = (agent: LibraryAgent) => {
    const assistantId = specialistAssistantId(agent.id);
    if (!assistantId) {
      openLibraryAgent(agent);
      return;
    }
    onNavigateAssistant(assistantId, { tab: agent.studio_enabled ? "studio" : "chat" });
  };

  const openDecisionQueue = () => {
    if (!systemProfile) return;
    onNavigateAssistant(systemProfile.id, { tab: "evolution" });
  };

  const requestSchemaUpgrade = async () => {
    if (requestingSchemaUpgrade || pendingSchemaUpgrade || !canRequestSchemaStandardUpgrade) return;
    setRequestingSchemaUpgrade(true);
    try {
      const res = await fetch("/api/evolution/proposals", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspace_id: osId,
          type: "schema_standard_upgrade",
          title: "Upgrade workspace screens",
          description: "Merge the current Agentic Operating System screen standard into this live Agentic Operating System. Existing rows and custom screens stay intact; missing fields, components, navigation entries, and standard metadata are filled in.",
          rationale: "The Agentic Operating System was born before the latest screen standard. It should catch codebase improvements through a Decision instead of staying stale or requiring a rebuild.",
          schema_diff: {
            target_standard_version: CURRENT_SCHEMA_STANDARD_VERSION,
            mode: "merge_screen_standard",
          },
          proposed_by: "architect",
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDecisions(null);
      setReloadNonce((n) => n + 1);
    } catch {
      const systemProfile = ASSISTANT_PROFILES.find((p) => p.is_system);
      if (systemProfile) {
        onNavigateAssistant(systemProfile.id, {
          tab: "chat",
          prompt: "Create a Decision to upgrade the Agentic Operating System to the latest Agentic Operating System screen standard. Preserve current rows and custom screens.",
        });
      }
    } finally {
      setRequestingSchemaUpgrade(false);
    }
  };

  const requestCodeBundleStandardReview = () => {
    const systemProfile = ASSISTANT_PROFILES.find((p) => p.is_system);
    if (!systemProfile) return;
    onNavigateAssistant(systemProfile.id, {
      tab: "chat",
      prompt: "Review this generated code-bundle Agentic Operating System against the latest Agentic Operating System standard. Adapt the live bundle screens, Mission Control wiring, agent workspace cards, receipts, decisions, and interactive CTAs without losing the current domain-specific screens or user data.",
    });
  };

  const handleLaunchReadinessAction = (item: LaunchReadinessItem) => {
    setExpandedReadinessActionId(null);
    if (item.action === "integrations") {
      onNavigateAssistant("architect", {
        tab: "integrations",
        prompt: "Help me connect or repair the live data sources the Agentic Operating System needs. Explain which connector is missing or broken, what permission I am granting, and where the connection will appear after approval.",
      });
      return;
    }
    if (item.action === "decisions") {
      if (systemProfile) onNavigateAssistant(systemProfile.id, { tab: "evolution" });
      return;
    }
    if (item.action === "schema") {
      if (needsCodeBundleStandardReview) {
        setCodeBundleDetailsOpen(true);
        return;
      }
      if (canRequestSchemaStandardUpgrade) {
        requestSchemaUpgrade();
        return;
      }
      if (systemProfile) {
        onNavigateAssistant(systemProfile.id, {
          tab: "chat",
          prompt: "Review whether the Agentic Operating System is on the latest Agentic Operating System screen standard. Explain what is missing before proposing any update.",
        });
      }
      return;
    }
    if (item.action === "operator") {
      onNavigateAssistant("operator", {
        tab: "chat",
        prompt: "Help me get the first real operating rows into the Agentic Operating System. Ask where the source data lives, then recommend the smallest approved connection path.",
      });
    }
  };

  const reviewLaunchReadinessAction = (item: LaunchReadinessItem) => {
    if (!item.action_detail) {
      handleLaunchReadinessAction(item);
      return;
    }
    setExpandedReadinessActionId((current) => current === item.id ? null : item.id);
  };

  const visibleLibraryAgents =
    libraryDepartment === "Recommended"
      ? guidedAgentMatches.map((match) => match.agent)
      : LIBRARY_AGENTS.filter((agent) => ALLOWED_LIBRARY_AGENT_IDS.has(agent.id) && agent.department === libraryDepartment && visibleLibraryDepartments.includes(agent.department));

  useEffect(() => {
    if (libraryDepartment === "Recommended") return;
    if (visibleLibraryDepartments.includes(libraryDepartment)) return;
    setLibraryDepartment("Recommended");
    setSelectedLibraryAgent(null);
  }, [libraryDepartment, visibleLibraryDepartments.join("|")]);

  // ── DECISIONS — pending proposals (owner-scoped server read) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const proposals = await fetchEvolutionProposals({ workspaceId: osId, statuses: ["pending"] });
        if (!cancelled) setDecisions((proposals as Proposal[]) || []);
      } catch (err) {
        console.warn("[MissionControl] failed to load pending proposals:", err);
        if (!cancelled) setDecisions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [osId, reloadNonce]);

  // ── CONNECTOR READINESS — Mission Control needs to know not just whether
  // connectors exist, but whether any are connected-but-unusable. ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [connectionsRes, healthRes] = await Promise.all([
          fetch(`/api/mcp/connections?workspace_id=${encodeURIComponent(osId)}`, {
            credentials: "include",
          }),
          fetch(`/api/mcp/health?workspace_id=${encodeURIComponent(osId)}`, {
            credentials: "include",
          }),
        ]);
        const connectionsJson = connectionsRes.ok ? await connectionsRes.json() : null;
        const healthJson = healthRes.ok ? await healthRes.json() : null;
        if (!cancelled) {
          setConnections(Array.isArray(connectionsJson?.connections) ? connectionsJson.connections : []);
          setConnectorHealth(healthJson?.health || {
            total_count: 0,
            ready_count: 0,
            repair_needed_count: 0,
            expired_count: 0,
            error_count: 0,
            missing_scope_count: 0,
            missing_capabilities: [],
            providers: [],
          });
        }
      } catch {
        if (!cancelled) {
          setConnections([]);
          setConnectorHealth({
            total_count: 0,
            ready_count: 0,
            repair_needed_count: 0,
            expired_count: 0,
            error_count: 0,
            missing_scope_count: 0,
            missing_capabilities: [],
            providers: [],
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [osId, reloadNonce]);

  // ── APPLIED AGENT DEPLOYMENTS — approved specialist Decisions become
  // visible active staff even before the future specialist runtime exists. ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const proposals = await fetchEvolutionProposals({
          workspaceId: osId,
          statuses: ["applied"],
          type: "agent_deployment",
        });
        if (!cancelled) setDeployedAgents((proposals as DeployedAgent[]) || []);
      } catch (err) {
        console.warn("[MissionControl] failed to load deployed agents:", err);
        if (!cancelled) setDeployedAgents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [osId, reloadNonce]);

  // ── THE PULSE — per-collection counts (owner-scoped via the server) ──
  // SF-3 — the old direct anon `screen_data` read returned EMPTY under RLS
  // (auth.uid()=null), so the pulse counts always read zero. Route through the
  // owner-gated counts endpoint (cookie carries auth).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const counts = new Map<string, number>();
      try {
        const res = await fetch(`/api/screen-data/${encodeURIComponent(osId)}/counts`, { credentials: "include" });
        const json = await res.json().catch(() => null);
        if (cancelled) return;
        if (res.ok && json?.ok) {
          for (const [sid, n] of Object.entries(json.counts as Record<string, number>)) {
            if (sid) counts.set(sid, Number(n) || 0);
          }
        }
      } catch {
        if (cancelled) return; // best-effort pulse — leave counts at zero on failure
      }
      const configuredScreens = Array.isArray(workspaceScreens) ? workspaceScreens : [];
      if (configuredScreens.length > 0) {
        const seen = new Set<string>();
        const configured = configuredScreens.map((screen) => {
          seen.add(screen.screen_id);
          return {
            screen_id: screen.screen_id,
            label: screen.label,
            count: counts.get(screen.screen_id) || 0,
          };
        });
        const importedExtras = Array.from(counts.entries())
          .filter(([screen_id]) => !seen.has(screen_id))
          .map(([screen_id, count]) => ({ screen_id, label: humanize(screen_id), count }));
        setPulse([...configured, ...importedExtras]);
        return;
      }
      setPulse(Array.from(counts.entries()).map(([screen_id, count]) => ({ screen_id, count })).sort((a, b) => b.count - a.count));
    })();
    return () => {
      cancelled = true;
    };
  }, [osId, reloadNonce, workspaceScreens]);

  // ── STAFF BOARD — latest action per assistant ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const assistantIds = [
        ...ASSISTANT_PROFILES.map((p) => p.id),
        ...((deployedAgents || [])
          .map((agent) => specialistAssistantId(agent.schema_diff?.agent_id))
          .filter(Boolean) as string[]),
      ];
      const entries = await Promise.all(
        assistantIds.map(async (assistantId) => {
          try {
            const r = await fetch(
              `/api/audit/actions?workspace_id=${encodeURIComponent(osId)}&assistant_id=${encodeURIComponent(assistantId)}`,
            );
            const d = await r.json();
            const latest = Array.isArray(d?.actions) && d.actions.length ? (d.actions[0] as ActionRow) : null;
            return [assistantId, latest] as const;
          } catch {
            return [assistantId, null] as const;
          }
        }),
      );
      if (!cancelled) setStaff(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [osId, reloadNonce, deployedAgents]);

  // ── STAFF BOARD — unread/new agent messages, including applied Agentic Operating System updates ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const assistantIds = [
        ...ASSISTANT_PROFILES.map((p) => p.id),
        ...((deployedAgents || [])
          .map((agent) => specialistAssistantId(agent.schema_diff?.agent_id))
          .filter(Boolean) as string[]),
      ];
      const entries = await Promise.all(
        assistantIds.map(async (assistantId) => {
          try {
            const r = await fetch(
              `/api/threads?workspace_id=${encodeURIComponent(osId)}&assistant_id=${encodeURIComponent(assistantId)}`,
              { credentials: "include" },
            );
            const d = await r.json();
            const threads = Array.isArray(d?.threads) ? d.threads : [];
            const unread = threads.reduce((sum: number, t: any) => sum + (Number(t?.unread_count) || 0), 0);
            const latest = threads[0] || null;
            return [assistantId, {
              unread_count: unread,
              last_message_preview: latest?.last_message_preview || null,
              last_message_at: latest?.last_message_at || latest?.created_at || null,
            }] as const;
          } catch {
            return [assistantId, null] as const;
          }
        }),
      );
      if (!cancelled) setStaffThreads(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [osId, reloadNonce, deployedAgents]);

  // ── RECEIPTS — 10 most recent actions across all assistants ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/audit/actions?workspace_id=${encodeURIComponent(osId)}`);
        const d = await r.json();
        if (!cancelled) setReceipts(Array.isArray(d?.actions) ? (d.actions as ActionRow[]).slice(0, 10) : []);
      } catch {
        if (!cancelled) setReceipts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [osId, reloadNonce]);

  if (libraryOpen) {
    return (
      <div ref={libraryContainerRef} className="relative h-full overflow-y-auto bg-[#0A0A0A] text-white">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-48"
          style={{ background: `radial-gradient(120% 100% at 50% 0%, ${accent}14 0%, transparent 70%)` }}
        />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-7 md:py-9">
          <FadeIn>
            <header className="mb-7">
              <button
                type="button"
                onClick={() => setLibraryOpen(false)}
                className="mb-5 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[12.5px] font-medium text-white/65 hover:text-white hover:border-white/20 hover:bg-white/[0.06] transition-colors"
              >
                <ArrowLeft size={14} />
                Mission Control
              </button>
              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
                <div className="max-w-2xl">
                  <h1 className="text-[26px] md:text-[32px] font-semibold tracking-tight text-white">Agent Library</h1>
                  <p className="text-[13.5px] text-white/50 mt-2 leading-relaxed">
                    Your active staff, recommended hires, and specialist agents you can request when the Agentic Operating System needs more hands.
                    New agents always go through Decisions before deployment.
                  </p>
                  <p className="text-[12px] text-white/35 mt-2">
                    Start with one agent for the job in front of you. Add more only when the work repeats and the current team is clearly blocked.
                  </p>
                  {libraryDepartment === "Recommended" && (
                    <p className="text-[12px] text-white/35 mt-2">
                      Recommended from the Agentic Operating System context: {recommendedDepartmentsText}.
                    </p>
                  )}
                </div>
                <div className={`${CARD} px-4 py-3 flex items-center gap-3`}>
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ background: `${accent}1f` }}
                  >
                    <Bot size={16} style={{ color: accent }} />
                  </div>
                  <div>
                    <div className="text-[18px] font-mono font-semibold leading-none">
                      {activeStaffCount}
                    </div>
                    <div className="text-[11px] text-white/40 mt-1">active staff</div>
                  </div>
                </div>
              </div>
            </header>
          </FadeIn>

          <FadeIn delay={40}>
            <section className={`${CARD} mb-6 px-4 py-4`}>
              {requestAgentFeedback && (
                <div
                  className={`mb-4 rounded-xl border px-4 py-3 text-[12.5px] ${
                    requestAgentFeedback.kind === "error"
                      ? "border-red-400/30 bg-red-500/10 text-red-100"
                      : "border-emerald-300/25 bg-emerald-500/10 text-emerald-100"
                  }`}
                >
                  {requestAgentFeedback.text}
                </div>
              )}
              <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
                <div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: `${accent}1f` }}
                    >
                      <Sparkles size={15} style={{ color: accent }} />
                    </div>
                    <div>
                      <div className="text-[14px] font-semibold text-white">Best next picks</div>
                      <div className="text-[12px] text-white/45">
                        The shortest path to the right specialist, without browsing the full catalog first.
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3">
                    {guidedAgentMatches.length === 0 ? (
                      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4">
                        <div className="text-[13px] font-semibold text-white">No exact specialist match yet</div>
                        <p className="mt-1 text-[12px] leading-relaxed text-white/45">
                          Start with the research path first. Once the Agentic Operating System has stronger context, Sloe will narrow this to the right hire for you.
                        </p>
                      </div>
                    ) : guidedAgentMatches.map(({ agent, reason, evidenceState }) => {
                      const isDeployed = deployedAgentIds.has(agent.id);
                      const isPending = pendingAgentIds.has(agent.id);
                      const requiresResearch = evidenceState === "research_required";
                      return (
                        <div
                          key={agent.id}
                          className="rounded-xl border border-amber-300/30 bg-[rgba(245,197,66,0.08)] px-4 py-4"
                        >
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="rounded-full border border-amber-300/35 bg-amber-300/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-100">
                                  {agent.department}
                                </span>
                                <span className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/55">
                                  {isDeployed ? "Active now" : isPending ? "Requested now" : requiresResearch ? "Research first" : "Best next hire"}
                                </span>
                              </div>
                              <div className="mt-2 text-[15px] font-semibold text-white">{agent.name}</div>
                              <p className="mt-1 text-[12.5px] leading-relaxed text-white/55">{reason}</p>
                            </div>
                            <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col">
                              {isDeployed ? (
                                <button
                                  type="button"
                                  onClick={() => openDeployedAgent(agent)}
                                  className="inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-[12.5px] font-semibold text-[#0a0a0a] transition-transform hover:scale-[1.02]"
                                  style={{ background: "#F5C542" }}
                                >
                                  Take me there
                                  <ChevronRight size={14} />
                                </button>
                              ) : isPending ? (
                                <div className="inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] px-4 py-2.5 text-[12px] font-semibold text-white/45">
                                  Requested in sidebar
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => requestAgent(agent)}
                                  disabled={!systemProfile || requestingAgentId === agent.id}
                                  className="inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] px-4 py-2.5 text-[12px] font-semibold text-white/70 transition-colors hover:bg-white/[0.07] hover:text-white disabled:opacity-50"
                                >
                                  {requestingAgentId === agent.id ? "Creating Decision..." : requiresResearch ? "Request research" : "Request agent"}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 border-t border-white/10 pt-3">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-white/28">
                      More available in the full library below
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg border border-white/10 bg-white/[0.04] flex items-center justify-center">
                      <UserPlus size={15} className="text-white/65" />
                    </div>
                    <div>
                      <div className="text-[14px] font-semibold text-white">Requested now</div>
                      <div className="text-[12px] text-white/45">
                        Anything already staged for Decision stays one click away here.
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3">
                    {pendingAgentRequests.length === 0 ? (
                      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4">
                        <div className="text-[13px] font-semibold text-white">Nothing queued</div>
                        <p className="mt-1 text-[12px] leading-relaxed text-white/45">
                          Once you request an agent, it will show up here with a direct path back to the exact card.
                        </p>
                      </div>
                    ) : pendingAgentRequests.slice(0, 4).map((request) => (
                      <div key={request.decisionId} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-medium uppercase tracking-wider text-white/35">
                              {request.department}
                            </div>
                            <div className="mt-1 text-[13.5px] font-semibold text-white">{request.name}</div>
                            <div className="mt-1 text-[11.5px] text-white/40">
                              Requested {relTime(request.createdAt)}
                            </div>
                            <button
                              type="button"
                              onClick={openDecisionQueue}
                              disabled={!systemProfile}
                              className="mt-3 inline-flex items-center justify-center gap-1 rounded-lg px-3 py-2 text-[12px] font-semibold text-[#0a0a0a] disabled:opacity-50"
                              style={{ background: "#F5C542" }}
                            >
                              Take me there
                              <ChevronRight size={12} />
                            </button>
                          </div>
                          <div className="inline-flex shrink-0 items-center rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[11.5px] font-semibold text-white/45">
                            Awaiting approval
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </FadeIn>

          <div className="grid grid-cols-1 lg:grid-cols-[0.78fr_1.22fr] gap-6">
            <FadeIn delay={80}>
              <section className={`${CARD} px-4 py-4`}>
                <SectionHeader>Active</SectionHeader>
                <div className="grid gap-2.5">
                  {ASSISTANT_PROFILES.filter((p) => !isAssistantHiddenFromNav(p.id)).map((p) => {
                    const initial = safeInitial(p.display_name);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => onNavigateAssistant(p.id)}
                        className="group w-full flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-3 text-left hover:border-white/20 hover:bg-white/[0.06] transition-colors"
                      >
                        <div
                          className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center text-[14px] font-bold"
                          style={{ background: `${accent}22`, color: accent }}
                        >
                          {initial}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="text-[14px] font-semibold text-white truncate">{p.display_name}</div>
                            <span className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/45">
                              {p.is_system ? "System" : "Worker"}
                            </span>
                          </div>
                          <div className="text-[12px] text-white/45 mt-1 leading-relaxed">{p.role_description}</div>
                        </div>
                        <ChevronRight size={15} className="mt-2 text-white/25 group-hover:text-white/50 transition-colors" />
                      </button>
                    );
                  })}
                  {(deployedAgents || []).map((agent) => {
                    const name = agent.schema_diff?.agent_name || agent.title.replace(/^Deploy\s+/i, "");
                    const department = agent.schema_diff?.department || "Specialist";
                    const initial = safeInitial(name);
                    const assistantId = specialistAssistantId(agent.schema_diff?.agent_id);
                    return (
                      <button
                        key={agent.id}
                        type="button"
                        onClick={() => assistantId && onNavigateAssistant(assistantId)}
                        disabled={!assistantId}
                        className="w-full flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-3 text-left hover:border-white/20 hover:bg-white/[0.06] disabled:hover:border-white/10 disabled:hover:bg-white/[0.03] transition-colors"
                      >
                        <div
                          className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center text-[14px] font-bold"
                          style={{ background: `${accent}22`, color: accent }}
                        >
                          {initial}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="text-[14px] font-semibold text-white truncate">{name}</div>
                            <span className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/45">
                              {department}
                            </span>
                          </div>
                          <div className="text-[12px] text-white/45 mt-1 leading-relaxed">
                            Approved specialist staff. Runtime actions stay behind approved integrations.
                          </div>
                        </div>
                        {assistantId && <ChevronRight size={15} className="mt-2 text-white/25" />}
                      </button>
                    );
                  })}
                </div>
              </section>
            </FadeIn>

            <FadeIn delay={140}>
              <section>
                <div className="mb-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    {["Recommended", ...visibleLibraryDepartments].map((dept) => {
                      const active = libraryDepartment === dept;
                      return (
                        <button
                          key={dept}
                          type="button"
                          onClick={() => {
                            setLibraryDepartment(dept);
                            setSelectedLibraryAgent(null);
                          }}
                          className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                            active
                              ? "border-transparent text-[#0a0a0a]"
                              : "border-white/10 bg-white/[0.03] text-white/50 hover:text-white hover:border-white/20"
                          }`}
                          style={active ? { background: accent } : undefined}
                        >
                          {dept}
                        </button>
                      );
                    })}
                  </div>
                  <div className="hidden md:flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[12px] text-white/35">
                    <Search size={13} />
                    Filter by department
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 items-start">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {visibleLibraryAgents.map((agent) => {
                      const selected = selectedLibraryAgent?.id === agent.id;
                      const isDeployed = deployedAgentIds.has(agent.id);
                      const isPending = pendingAgentIds.has(agent.id);
                      const contextualMatch = recommendationMatchForAgent(agent, contextualRecommendedMatches);
                      const recommendationMatch = libraryDepartment === "Recommended"
                        ? contextualMatch
                        : agent.recommended
                          ? contextualMatch || {
                              agent,
                              evidenceState: "known_context" as const,
                              reason: `${agent.name} is the recommended ${agent.department} specialist for this kind of work.`,
                            }
                          : null;
                      const reason = recommendationMatch?.reason || null;
                      const requiresResearch = recommendationMatch?.evidenceState === "research_required";
                      const recommendedHighlight = Boolean(
                        libraryDepartment === "Recommended" &&
                        recommendationMatch &&
                        !requiresResearch &&
                        guidedAgentIds.has(agent.id),
                      );
                      return (
                        <div
                          key={agent.id}
                          onClick={() => setSelectedLibraryAgent(selected ? null : agent)}
                          onKeyDown={(e) => {
                            if (e.target !== e.currentTarget) return;
                            if (e.key !== "Enter" && e.key !== " ") return;
                            e.preventDefault();
                            setSelectedLibraryAgent(selected ? null : agent);
                          }}
                          role="button"
                          tabIndex={0}
                          aria-pressed={selected}
                          className={`${CARD_INTERACTIVE} group text-left px-4 py-4 ${
                            selected ? "md:col-span-2 min-h-[232px] border-white/25 bg-white/[0.07]" : "min-h-[156px]"
                          } ${
                            recommendedHighlight
                              ? "border-amber-300/45 bg-[rgba(245,197,66,0.10)] shadow-[0_0_34px_rgba(245,197,66,0.10)] hover:border-amber-300/65 hover:bg-[rgba(245,197,66,0.14)]"
                              : ""
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div
                              className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center"
                              style={{ background: recommendedHighlight ? "rgba(245, 197, 66, 0.20)" : `${accent}1f` }}
                            >
                              <UserPlus size={16} style={{ color: recommendedHighlight ? "#F5C542" : accent }} />
                            </div>
                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10.5px] ${
                              recommendedHighlight
                                ? "border-amber-300/35 bg-amber-300/15 text-amber-100"
                                : "border-white/10 bg-white/[0.04] text-white/45"
                            }`}>
                              {requiresResearch ? <Search size={10} /> : <LockKeyhole size={10} />}
                              {isDeployed ? "Active" : isPending ? "Requested" : requiresResearch ? "Research first" : recommendedHighlight ? "Recommended" : "Decision"}
                            </span>
                          </div>
                          <div className="mt-3">
                            <div className="text-[10px] font-medium uppercase tracking-wider text-white/35">
                              {agent.department}
                            </div>
                            <div className="text-[14px] font-semibold text-white mt-0.5">{agent.name}</div>
                            <div className={`text-[12px] text-white/50 mt-1.5 leading-relaxed ${selected ? "" : "line-clamp-2"}`}>
                              {agent.description}
                            </div>
                            {reason && (
                              <div className={`mt-3 rounded-lg border px-3 py-2 text-[11.5px] leading-relaxed ${
                                recommendedHighlight
                                  ? "border-amber-300/25 bg-amber-300/[0.08] text-amber-50/75"
                                  : "border-white/10 bg-white/[0.035] text-white/45"
                              } ${selected ? "" : "line-clamp-2"}`}>
                                {reason}
                              </div>
                            )}
                          </div>

                          {selected ? (
                            <div className="mt-5 grid gap-3">
                              <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-3">
                                <div className="text-[11px] font-medium uppercase tracking-wider text-white/35">
                                  {requiresResearch ? "Research path" : "Deployment path"}
                                </div>
                                <div className="text-[12.5px] text-white/55 leading-relaxed mt-1">
                                  {requiresResearch
                                    ? "This starts with evidence gathering, source verification, workflow mapping, and skill-gap diagnosis. Any new specialist agent, screen, or skill still comes back as a Decision before it joins the Agentic Operating System."
                                    : "Architect creates a Decision card first. The user approves before this agent joins the Agentic Operating System."}
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="text-[12px] text-white/40">
                                  {requiresResearch
                                    ? "Output: research brief, sources, recommended skill file, and Decision proposal."
                                    : "Access: workspace data and approved integrations only."}
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2">
                                  {isDeployed ? (
                                    <button
                                      type="button"
                                      onClick={() => openDeployedAgent(agent)}
                                      className="inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-[12.5px] font-semibold text-[#0a0a0a] transition-transform hover:scale-[1.02]"
                                      style={{ background: accent }}
                                    >
                                      Take me there
                                      <ChevronRight size={14} />
                                    </button>
                                  ) : isPending ? (
                                    <div className="inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] px-4 py-2.5 text-[12px] font-semibold text-white/45">
                                      In sidebar
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        requestAgent(agent);
                                      }}
                                      disabled={!systemProfile || requestingAgentId === agent.id}
                                      className="inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-[12.5px] font-semibold text-[#0a0a0a] transition-transform hover:scale-[1.02] disabled:opacity-50"
                                      style={{ background: accent }}
                                    >
                                      {requestingAgentId === agent.id
                                        ? "Creating Decision..."
                                        : requiresResearch
                                          ? "Request research"
                                          : "Request agent"}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-white/45 group-hover:text-white">
                              View details
                              <ChevronRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            </FadeIn>
          </div>
        </div>
      </div>
    );
  }

  // ── Declutter: partition the Operating checklist for promote-and-collapse. ──
  const checklistActionItems = launchReadinessItems.filter((i) => i.status === "needs_action");
  const checklistDoneItems = launchReadinessItems.filter((i) => i.status !== "needs_action"); // ready + watching
  // The single top-priority pending DATA action (connectors first, then workspace_data).
  const promotedDataItem =
    launchReadinessItems.find(
      (i) => (i.id === "connectors" || i.id === "workspace_data") && i.status === "needs_action",
    ) || null;
  const requiredDataSatisfied = !promotedDataItem;
  // Adaptive top "do this now" slot: a pending required-data action wins it;
  // otherwise the Needs Attention triage prompt is promoted there. Exactly one.
  const needsAttentionPromoted = requiredDataSatisfied;
  // Remaining pending checklist cards = needs_action minus the promoted data card.
  const remainingActionItems = checklistActionItems.filter((i) => i !== promotedDataItem);
  const pulseScreenCount = pulse?.filter((item) => item.count > 0).length || 0;
  const pulseRowCount = pulse?.reduce((sum, item) => sum + item.count, 0) || 0;
  const topPulse = pulse?.[0] || null;
  const visibleReceipts = (receipts || []).slice(0, 5);

  const renderStatusTile = (
    title: string,
    value: string,
    detail: string,
    sourceLabel: string,
    actionLabel?: string,
    onAction?: () => void,
    accentTone?: "brand" | "success" | "muted",
  ) => {
    const tone =
      accentTone === "success"
        ? { bg: "rgba(16,185,129,0.14)", color: "#34d399", border: "border-emerald-400/20" }
        : accentTone === "muted"
          ? { bg: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.7)", border: "border-white/10" }
          : { bg: `${accent}18`, color: accent, border: "border-white/10" };
    return (
      <div
        className={`rounded-xl border ${tone.border} bg-white/[0.03] px-4 py-4 flex flex-col gap-3 ${
          actionLabel && onAction ? "cursor-pointer transition-colors hover:bg-white/[0.05]" : ""
        }`}
      >
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/35">{title}</div>
          <div className="mt-1.5 text-[22px] font-semibold text-white tabular-nums">{value}</div>
          <div className="mt-1 text-[11.5px] leading-relaxed text-white/40">{detail}</div>
          <div className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-white/25">{sourceLabel}</div>
        </div>
        {actionLabel && onAction && (
          <button
            type="button"
            onClick={onAction}
            className="inline-flex items-center gap-1.5 self-start rounded-lg px-3 py-1.5 text-[11.5px] font-semibold transition-colors hover:bg-white/[0.06]"
            style={{ background: tone.bg, color: tone.color }}
          >
            {actionLabel}
            <ChevronRight size={12} />
          </button>
        )}
      </div>
    );
  };

  const scrollToSection = (sectionId: string) => {
    if (typeof document === "undefined") return;
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Full checklist card (reused for the promoted top card AND remaining actions) —
  // body is unchanged from the previous inline render.
  const renderReadinessCard = (item: LaunchReadinessItem) => {
    const ready = item.status === "ready";
    const watching = item.status === "watching";
    const Icon = ready ? CheckCircle2 : watching ? Wifi : CircleAlert;
    const expanded = expandedReadinessActionId === item.id;
    return (
      <div
        key={item.id}
        className={`${CARD} px-4 py-4 min-h-[126px] flex flex-col justify-between gap-4 ${
          ready ? "border-emerald-400/20" : watching ? "border-white/[0.06] opacity-80" : "border-white/30"
        }`}
      >
        <div>
          <div className="flex items-start gap-3">
            <div
              className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
              style={{
                background: ready ? "rgba(16,185,129,0.14)" : watching ? "rgba(255,255,255,0.06)" : `${accent}20`,
                color: ready ? "#34d399" : watching ? "rgba(255,255,255,0.55)" : accent,
              }}
            >
              <Icon size={15} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-[13.5px] font-semibold text-white">{item.title}</div>
                {!ready && (
                  <span
                    className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                      watching ? "text-white/40 bg-white/[0.06]" : "text-[#0a0a0a]"
                    }`}
                    style={watching ? undefined : { background: accent }}
                  >
                    {watching ? "Optional" : "Required"}
                  </span>
                )}
              </div>
              <p className="mt-1 text-[12px] leading-relaxed text-white/45">{item.detail}</p>
            </div>
          </div>
        </div>
        {expanded && item.action_detail && (
          <div className="rounded-lg border border-white/10 bg-white/[0.035] px-3 py-3">
            <div className="grid gap-2">
              {[
                ["Why", item.action_detail.why],
                ["Involves", item.action_detail.involves],
                ["What happens", item.action_detail.effect],
                ["Approval", item.action_detail.approval],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-white/35">{label}</div>
                  <p className="mt-0.5 text-[11.5px] leading-relaxed text-white/50">{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {item.cta && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => reviewLaunchReadinessAction(item)}
              aria-expanded={expanded}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[12px] font-semibold text-white/70 hover:text-white hover:border-white/20 hover:bg-white/[0.07] transition-colors"
            >
              {expanded ? "Hide details" : "Review action"}
              <ChevronRight size={12} className={expanded ? "rotate-90 transition-transform" : "transition-transform"} />
            </button>
            {expanded && (
              <button
                type="button"
                onClick={() => handleLaunchReadinessAction(item)}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold text-[#0a0a0a] transition-transform hover:scale-[1.02]"
                style={{ background: accent }}
              >
                {item.cta}
                <ChevronRight size={12} />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Satisfied / optional items collapse to a compact tick chip.
  const renderReadinessTick = (item: LaunchReadinessItem) => {
    const ready = item.status === "ready";
    const Icon = ready ? CheckCircle2 : Wifi;
    return (
      <span
        key={item.id}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[12px] text-white/55"
      >
        <Icon size={13} className={ready ? "text-emerald-400" : "text-white/40"} />
        {item.title}
      </span>
    );
  };

  // Needs Attention prompt — reused for the promoted top slot AND its normal grid
  // position. Body unchanged from the previous inline render.
  const renderNeedsAttentionCard = () => (
    <div
      onClick={() =>
        operatorProfile &&
        onNavigateAssistant(operatorProfile.id, { tab: "chat" })
      }
      className={`${operatorProfile ? CARD_INTERACTIVE : CARD} group h-full flex flex-col justify-between gap-4 px-5 py-5`}
    >
      <div>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: `${accent}1f` }}>
          <Sparkles size={16} style={{ color: accent }} />
        </div>
        <div className="text-[15px] font-medium text-white">
          Tell {operatorProfile?.display_name || "The Operator"} what you want to focus on this week
        </div>
        <div className="text-[12.5px] text-white/55 mt-1 leading-relaxed">
          A triage read on what's most worth your time this week — pulled from your live data.
        </div>
      </div>
      {operatorProfile && (
        <div
          className="inline-flex items-center gap-1.5 self-start px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-[#0a0a0a] transition-transform group-hover:scale-[1.03]"
          style={{ background: accent }}
        >
          <Sparkles size={13} />
          Triage my week
        </div>
      )}
    </div>
  );

  return (
    <div ref={mainContainerRef} className="relative h-full overflow-y-auto bg-[#0A0A0A] text-white">
      {/* Ambient brand wash at the very top — barely-there, premium. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-48"
        style={{ background: `radial-gradient(120% 100% at 50% 0%, ${accent}14 0%, transparent 70%)` }}
      />
      <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-7 md:py-9">
        {/* ── HEADER — greeting block ── */}
        <FadeIn>
          <header className="flex items-start justify-between gap-4 mb-8">
            <div className="min-w-0">
              <h1 className="text-[24px] md:text-[28px] font-semibold tracking-tight text-white truncate">
                {greeting}
                {userFirstName ? `, ${userFirstName}` : ""}
              </h1>
              <p className="text-[13px] text-white/45 mt-1.5">
                <span className="text-white/70">{osName}</span>
                <span className="text-white/25"> · </span>
                {todayStr}
              </p>
              <p className="text-[12px] text-white/35 mt-2 max-w-2xl leading-relaxed">
                Mission Control gives you one clear starting point, then keeps the live workspace, connectors, and staff signals in view without making you manage the whole system at once.
              </p>
            </div>
            <button
              type="button"
              onClick={refresh}
              aria-label="Refresh"
              title="Refresh"
              className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-white/50 border border-white/10 bg-white/[0.03] hover:text-white hover:border-white/20 hover:bg-white/[0.06] transition-colors"
            >
              <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
            </button>
          </header>
        </FadeIn>

        {/* ── 1. START HERE — one primary move for the week ── */}
        <FadeIn delay={50}>
          <section className="mb-8">
            <SectionHeader
              right={
                !launchChecklistLoading ? (
                  <span
                    className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                    style={{
                      background: launchNeedsActionCount === 0 ? "rgba(16,185,129,0.14)" : `${accent}22`,
                      color: launchNeedsActionCount === 0 ? "#34d399" : accent,
                    }}
                  >
                    {launchReadyCount}/{launchReadinessItems.length} ready
                  </span>
                ) : undefined
              }
            >
              Start here
            </SectionHeader>
            {launchChecklistLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <Shimmer key={i} className="h-[126px]" />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)]">
                  {/* TOP "DO THIS NOW" SLOT — exactly one occupant: a pending
                      required-data action if any, else the promoted Needs Attention
                      triage prompt. */}
                  {promotedDataItem
                    ? renderReadinessCard(promotedDataItem)
                    : needsAttentionPromoted
                      ? renderNeedsAttentionCard()
                      : null}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {renderStatusTile(
                      "Connector health",
                      connectorHealth ? `${connectorHealth.ready_count}/${connectorHealth.total_count}` : "—",
                      connectorHealth
                        ? connectorHealth.repair_needed_count === 0
                          ? "All connectors are healthy."
                          : `${connectorHealth.repair_needed_count} connector${connectorHealth.repair_needed_count === 1 ? "" : "s"} need repair or more scope.`
                        : "Loading connector status.",
                      "Proof: /api/integrations + connector status",
                      "See integrations",
                      () => onNavigateAssistant("architect", { tab: "integrations" }),
                      connectorHealth && connectorHealth.repair_needed_count === 0 ? "success" : "brand",
                    )}
                    {renderStatusTile(
                      "Decisions",
                      decisions ? String(decisions.length) : "—",
                      decisions && decisions.length === 0
                        ? "Nothing is waiting on approval."
                        : decisions
                          ? "Pending changes are ready for review."
                          : "Loading approvals.",
                      "Proof: pending evolution proposals",
                      "See decisions",
                      () => systemProfile && onNavigateAssistant(systemProfile.id, { tab: "evolution" }),
                      decisions && decisions.length === 0 ? "success" : "brand",
                    )}
                    {renderStatusTile(
                      "Workspace screens",
                      pulse ? String(pulseScreenCount) : "—",
                      pulse
                        ? topPulse
                          ? `${pulseRowCount} live row${pulseRowCount === 1 ? "" : "s"} across ${pulse.length} screen${pulse.length === 1 ? "" : "s"}.`
                          : "No live rows yet."
                        : "Loading screen activity.",
                      "Proof: screen_data counts",
                      "See staff board",
                      () => scrollToSection("mission-control-staff-board"),
                      pulse && pulseRowCount > 0 ? "success" : "muted",
                    )}
                    {renderStatusTile(
                      "Receipts",
                      receipts ? String(receipts.length) : "—",
                      receipts && receipts.length > 0
                        ? `${receipts.length} recent action${receipts.length === 1 ? "" : "s"} are recorded.`
                        : "Waiting for activity.",
                      "Proof: /api/audit/actions",
                      "See receipts",
                      () => scrollToSection("mission-control-receipts"),
                      receipts && receipts.length > 0 ? "success" : "muted",
                    )}
                  </div>
                </div>
                {/* Remaining pending actions keep their detail, but lower on the page. */}
                {remainingActionItems.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {remainingActionItems.map((item) => renderReadinessCard(item))}
                  </div>
                )}
                {/* Satisfied / optional items collapse to a compact tick strip. */}
                {checklistDoneItems.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {checklistDoneItems.map((item) => renderReadinessTick(item))}
                  </div>
                )}
              </div>
            )}
          </section>
        </FadeIn>

        {/* ── 2. STAFF BOARD — where the work lives ── */}
        <FadeIn delay={180}>
          <section id="mission-control-staff-board" className="mb-8 scroll-mt-6">
            <SectionHeader>Staff Board</SectionHeader>
            <div className="grid gap-3">
              {ASSISTANT_PROFILES.filter((p) => !isAssistantHiddenFromNav(p.id)).map((p) => {
                const initial = safeInitial(p.display_name);
                const loaded = staff !== null;
                const latest = staff?.[p.id] ?? null;
                const threadSignal = staffThreads?.[p.id] ?? null;
                const unread = threadSignal?.unread_count || 0;
                return (
                  <div
                    key={p.id}
                    onClick={() => onNavigateAssistant(p.id)}
                    className={`${CARD_INTERACTIVE} group flex items-start gap-3 px-4 py-3 overflow-hidden w-full min-w-0 max-w-full`}
                  >
                    <div
                      className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-[14px] font-bold ring-1"
                      style={{ background: `${accent}26`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}33` }}
                    >
                      {initial}
                    </div>
                    <div className="flex-1 min-w-0 max-w-full">
                      <div className="flex items-center gap-2 min-w-0 max-w-full overflow-hidden">
                        <span className="text-[14px] font-medium text-white truncate min-w-0 max-w-full">{p.display_name}</span>
                        {unread > 0 && (
                          <span
                            className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold text-[#0a0a0a]"
                            style={{ background: accent }}
                          >
                            {unread} new
                          </span>
                        )}
                        <span className="hidden xl:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white/55 bg-white/10 border border-white/10 shrink-0 max-w-[118px]">
                          <Layers size={9} />
                          <span className="truncate">{p.engine_badge}</span>
                        </span>
                      </div>
                      <div className="text-[12px] text-white/45 mt-0.5 truncate max-w-full">{p.role_description}</div>
                      <div className="text-[11.5px] text-white/35 mt-0.5 truncate max-w-full">
                        {unread > 0 && threadSignal?.last_message_preview ? (
                          <>
                            <span style={{ color: accent }}>New message</span>
                            <span className="text-white/20"> · </span>
                            {threadSignal.last_message_preview}
                          </>
                        ) : !loaded ? (
                          <span className="inline-block w-32 h-3 align-middle animate-pulse rounded bg-white/[0.06]" />
                        ) : latest ? (
                          <>
                            {actionLabel(latest.tool_called)}
                            <span className="text-white/20"> · </span>
                            <span className="font-mono">{relTime(latest.created_at)}</span>
                          </>
                        ) : p.id === "architect" ? (
                          "I can add screens, evolve workflows, and structure your OS."
                        ) : (
                          "No actions yet"
                        )}
                      </div>
                      {p.id === "architect" && loaded && !latest && unread === 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigateAssistant("architect", {
                              tab: "chat",
                              prompt: "Review this Agentic Operating System for missing screens, fields, agents, or permissions.",
                            });
                          }}
                          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-[#0a0a0a] transition-transform hover:scale-[1.03]"
                          style={{ background: accent }}
                        >
                          <Sparkles size={12} />
                          Set up my workspace
                        </button>
                      )}
                    </div>
                    <ChevronRight size={16} className="shrink-0 text-white/25 group-hover:text-white/50 transition-colors" />
                  </div>
                );
              })}
              {(deployedAgents || []).map((agent) => {
                const name = agent.schema_diff?.agent_name || agent.title.replace(/^Deploy\s+/i, "");
                const department = agent.schema_diff?.department || "Specialist";
                const initial = safeInitial(name);
                const assistantId = specialistAssistantId(agent.schema_diff?.agent_id);
                const threadSignal = assistantId ? staffThreads?.[assistantId] ?? null : null;
                const latest = assistantId ? staff?.[assistantId] ?? null : null;
                const unread = threadSignal?.unread_count || 0;
                return (
                  <button
                    key={agent.id}
                    type="button"
                    onClick={() => assistantId && onNavigateAssistant(assistantId)}
                    disabled={!assistantId}
                    className={`${assistantId ? CARD_INTERACTIVE : CARD} flex items-start gap-3 px-4 py-3 overflow-hidden w-full min-w-0 max-w-full text-left disabled:cursor-default`}
                  >
                    <div
                      className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-[14px] font-bold ring-1"
                      style={{ background: `${accent}26`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}33` }}
                    >
                      {initial}
                    </div>
                    <div className="flex-1 min-w-0 max-w-full">
                      <div className="flex items-center gap-2 min-w-0 max-w-full overflow-hidden">
                        <span className="text-[14px] font-medium text-white truncate min-w-0 max-w-full">{name}</span>
                        {unread > 0 && (
                          <span
                            className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold text-[#0a0a0a]"
                            style={{ background: accent }}
                          >
                            {unread} new
                          </span>
                        )}
                        <span className="hidden xl:inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white/55 bg-white/10 border border-white/10 shrink-0 max-w-[118px]">
                          {department}
                        </span>
                      </div>
                      <div className="text-[12px] text-white/45 mt-0.5 truncate max-w-full">
                        Specialist staff approved by Decision
                      </div>
                      <div className="text-[11.5px] text-white/35 mt-0.5 truncate max-w-full">
                        {unread > 0 && threadSignal?.last_message_preview ? (
                          <>
                            <span style={{ color: accent }}>New message</span>
                            <span className="text-white/20"> · </span>
                            {threadSignal.last_message_preview}
                          </>
                        ) : latest ? (
                          <>
                            {actionLabel(latest.tool_called)}
                            <span className="text-white/20"> · </span>
                            <span className="font-mono">{relTime(latest.created_at)}</span>
                          </>
                        ) : (
                          "No specialist actions yet"
                        )}
                      </div>
                    </div>
                    {assistantId && <ChevronRight size={16} className="shrink-0 text-white/25 group-hover:text-white/50 transition-colors" />}
                  </button>
                );
              })}
            </div>
          </section>
        </FadeIn>

        {/* ── 3. RECEIPTS — timeline ── */}
        <FadeIn delay={300}>
          <section id="mission-control-receipts" className="mb-2 scroll-mt-6">
            <SectionHeader>Receipts</SectionHeader>
            {receipts === null ? (
              <div className="grid gap-2 pl-4">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Shimmer key={i} className="h-[34px]" />
                ))}
              </div>
            ) : receipts.length === 0 ? (
              <p className="text-[12px] text-white/40">No moves yet — every action your staff takes lands here.</p>
            ) : (
              <>
                <ol className="relative ml-2 border-l border-white/10">
                  {visibleReceipts.map((a) => {
                    const navAssistant = receiptNavAssistant(a);
                    const isUpdate = a.tool_called === "schema_evolution_applied";
                    return (
                      <li key={a.id} className="relative pl-5 py-2.5">
                        <span
                          className="absolute -left-[5px] top-3.5 w-2.5 h-2.5 rounded-full ring-2 ring-[#0A0A0A]"
                          style={{ background: accent, opacity: assistantOpacity(a.assistant_id) }}
                        />
                        <button
                          type="button"
                          disabled={!navAssistant}
                          onClick={() => navAssistant && onNavigateAssistant(navAssistant, { tab: "chat" })}
                          className="group w-full min-w-0 text-left disabled:cursor-default"
                        >
                          <div className="flex items-baseline justify-between gap-3">
                            <div className="min-w-0 text-[13px] text-white/80 truncate">
                              <span className="text-white/45">{assistantName(a.assistant_id)}</span>
                              <span className="text-white/25"> — </span>
                              {receiptSummary(a)}
                            </div>
                            <time className="shrink-0 text-[11px] font-mono text-white/35 tabular-nums">
                              {relTime(a.created_at)}
                            </time>
                          </div>
                          {isUpdate && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold transition-colors group-hover:text-white/85" style={{ color: accent }}>
                              Open update message
                              <ChevronRight size={11} />
                            </div>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ol>
                {receipts.length > visibleReceipts.length && (
                  <p className="mt-3 text-[12px] text-white/35">
                    {receipts.length - visibleReceipts.length} older receipt{receipts.length - visibleReceipts.length === 1 ? "" : "s"} hidden.
                  </p>
                )}
              </>
            )}
          </section>
        </FadeIn>

        {/* ── 4. AGENT LIBRARY — secondary staffing expansion ── */}
        <FadeIn delay={360}>
          <section id="mission-control-agent-library" className="mb-8 scroll-mt-6">
            <SectionHeader
              right={
                <button
                  type="button"
                  onClick={() => setLibraryOpen(true)}
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-white/45 hover:text-white transition-colors"
                >
                  View full agent library
                  <ChevronRight size={12} />
                </button>
              }
            >
              Agent Library
            </SectionHeader>

            <div className={`${CARD} px-4 py-4`}>
              {requestAgentFeedback && (
                <div
                  className={`mb-4 rounded-xl border px-4 py-3 text-[12.5px] ${
                    requestAgentFeedback.kind === "error"
                      ? "border-red-400/30 bg-red-500/10 text-red-100"
                      : "border-emerald-300/25 bg-emerald-500/10 text-emerald-100"
                  }`}
                >
                  {requestAgentFeedback.text}
                </div>
              )}
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
                      style={{ background: `${accent}1f` }}
                    >
                      <Bot size={15} style={{ color: accent }} />
                    </div>
                    <div>
                      <div className="text-[14px] font-semibold text-white">Optional staff expansion</div>
                      <div className="text-[12px] text-white/45">
                        {activeStaffCount} active now · {guidedAgentMatches.length} recommended next
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 max-w-xl text-[12.5px] text-white/45 leading-relaxed">
                    Recommended agents are staged as Decisions first, so nothing joins the Agentic Operating System without approval.
                    {contextualRecommendedMatches.length > 0 ? ` Current match: ${recommendedDepartmentsText}.` : ""}
                  </p>
                  <p className="mt-2 max-w-xl text-[12px] text-white/35 leading-relaxed">
                    Use this when the current job needs a specialist. Otherwise, stay with The Operator and the workspace screens above.
                  </p>
                  <button
                    type="button"
                    onClick={() => setLibraryExpanded((v) => !v)}
                    className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-white/55 hover:text-white transition-colors"
                  >
                    {libraryExpanded ? "Hide recommendations" : `Show recommendations (${guidedAgentMatches.length})`}
                    <ChevronRight size={12} className={libraryExpanded ? "rotate-90 transition-transform" : "transition-transform"} />
                  </button>
                </div>

                <div className={`grid grid-cols-1 gap-3 ${libraryExpanded || pendingAgentRequests.length > 0 ? "lg:w-[560px]" : ""}`}>
                  {libraryExpanded && (contextualRecommendedMatches.length === 0 ? (
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4">
                      <div className="text-[13.5px] font-semibold text-white">No specialist match yet</div>
                      <p className="mt-1 text-[12px] leading-relaxed text-white/45">
                        Once the Agentic Operating System has a clear tracked noun and workspace screens, recommended staff will appear here.
                      </p>
                    </div>
                  ) : guidedAgentMatches.slice(0, 3).map(({ agent, reason, evidenceState }) => {
                    const isDeployed = deployedAgentIds.has(agent.id);
                    const isPending = pendingAgentIds.has(agent.id);
                    const requiresResearch = evidenceState === "research_required";
                    return (
                      <div
                        key={agent.id}
                        className="rounded-xl border border-amber-300/25 bg-[rgba(245,197,66,0.08)] px-4 py-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-[10px] font-medium uppercase tracking-wider text-amber-100/70">
                            {agent.department}
                          </div>
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/35 bg-amber-300/15 px-2 py-0.5 text-[10px] text-amber-100">
                            {requiresResearch ? <Search size={9} /> : <LockKeyhole size={9} />}
                            {isDeployed ? "Active" : isPending ? "Requested" : requiresResearch ? "Research first" : "Decision"}
                          </span>
                        </div>
                        <div className="text-[13.5px] font-semibold text-white mt-2">{agent.name}</div>
                        <div className="mt-1.5 text-[11.5px] leading-relaxed text-amber-50/80 line-clamp-2">{reason}</div>
                          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                            {isDeployed ? (
                              <button
                                type="button"
                                onClick={() => openDeployedAgent(agent)}
                                className="inline-flex items-center justify-center gap-1 rounded-lg px-3 py-2 text-[12px] font-semibold text-[#0a0a0a]"
                                style={{ background: "#F5C542" }}
                              >
                                Take me there
                                <ChevronRight size={12} />
                              </button>
                            ) : isPending ? (
                              <div className="inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[12px] font-semibold text-white/45">
                                Requested in sidebar
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => requestAgent(agent)}
                                disabled={!systemProfile || requestingAgentId === agent.id}
                                className="inline-flex items-center justify-center rounded-lg border border-amber-300/35 bg-amber-300/10 px-3 py-2 text-[12px] font-semibold text-amber-100 disabled:opacity-50"
                              >
                                {requestingAgentId === agent.id ? "Creating..." : requiresResearch ? "Request research" : "Request agent"}
                              </button>
                            )}
                          </div>
                      </div>
                    );
                  }))}
                  {pendingAgentRequests.length > 0 && (
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4">
                      <div className="text-[12px] font-semibold uppercase tracking-wider text-white/35">Requested now</div>
                      <div className="mt-3 grid gap-2">
                        {pendingAgentRequests.slice(0, 2).map((request) => (
                          <div key={request.decisionId} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.035] px-3 py-2.5">
                            <div className="min-w-0">
                              <div className="text-[12.5px] font-semibold text-white truncate">{request.name}</div>
                              <div className="text-[11px] text-white/40">{request.department} · {relTime(request.createdAt)}</div>
                              <button
                                type="button"
                                onClick={openDecisionQueue}
                                disabled={!systemProfile}
                                className="mt-2 inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11.5px] font-semibold text-[#0a0a0a] disabled:opacity-50"
                                style={{ background: "#F5C542" }}
                              >
                                Take me there
                                <ChevronRight size={11} />
                              </button>
                            </div>
                            <div className="inline-flex shrink-0 items-center rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[11.5px] font-semibold text-white/45">
                              Awaiting approval
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setLibraryOpen(true)}
                className="mt-4 w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-[13px] font-semibold text-white/70 hover:text-white hover:border-white/20 hover:bg-white/[0.06] transition-colors"
              >
                View full agent library
              </button>
            </div>
          </section>
        </FadeIn>
      </div>
    </div>
  );
}
